<template>
  <div class="m-page">
    <div class="m-back m-back--split">
      <el-button link type="primary" @click="goBack">{{ backButtonLabel }}</el-button>
      <el-button v-if="order && viewMode === 'item'" link type="primary" @click="goToToc">↑ 返回明细目录</el-button>
    </div>

    <div v-loading="loading" class="m-body">
      <template v-if="order">
        <section class="m-hero">
          <div class="m-hero__no">{{ order.dispatch_order_no }}</div>
          <div class="m-hero__sub">
            <span>{{ headerCustomer }}</span>
            <el-tag size="small" :type="heroStatusDisplay.tag">{{ heroStatusDisplay.text }}</el-tag>
          </div>
          <div v-if="orderSeasonTypesHero" class="m-hero__season muted">{{ orderSeasonTypesHero }}</div>
          <div
            v-if="dispatchHeroDue.kind !== 'none'"
            class="m-hero__due"
            :class="dispatchHeroDue.lineClass"
          >
            <el-tag v-if="dispatchHeroDue.label" size="small" :type="dispatchHeroDue.tagType">{{
              dispatchHeroDue.label
            }}</el-tag>
            <span v-if="dispatchHeroDue.dateLine" class="m-hero__due-time">{{ dispatchHeroDue.dateLine }}</span>
          </div>
          <div v-if="qtySummary" class="m-hero__sum">
            <div v-if="viewMode === 'item'" class="m-hero__sum-row">
              <span class="lbl">本行剩余应打</span>
              <span class="val">{{ fmtNumMaybe1(lineRemainingQtyDisplay) }} {{ branchQtyUnitLabel }}</span>
            </div>
            <div v-else class="m-hero__sum-row muted">
              <span class="lbl">本行剩余应打</span>
              <span class="val">进入某条明细后显示</span>
            </div>
            <div class="m-hero__sum-row strong">
              <span class="lbl">全单剩余应打</span>
              <span class="val">{{ fmtNumMaybe1(dispatchRemainingQtyDisplay) }} {{ branchQtyUnitLabel }}</span>
            </div>
          </div>
          <p v-if="order.warehouse_completed_at" class="m-hero__done">
            现场完成 {{ formatDt(order.warehouse_completed_at) }}
            <template v-if="order.warehouse_completed_by_name"> · {{ order.warehouse_completed_by_name }}</template>
          </p>
          <p v-if="heroBizAuditHint" class="m-hero__edit-tip">{{ heroBizAuditHint }}</p>
        </section>

        <section
          v-if="showWarehouseBizSubmitCard"
          class="m-section m-section--biz-submit"
        >
          <h3 class="m-h3">
            {{ isProcessingOutboundDispatch ? '仓库整单 · 提交加工出库' : '仓库整单 · 提交业务员核销' }}
          </h3>
          <template v-if="isProcessingOutboundDispatch ? dispatchRemainingQty <= BRANCH_QTY_MATCH_EPS : (allSlittingLinesConfirmed && dispatchRemainingQty <= BRANCH_QTY_MATCH_EPS)">
            <p class="m-hint">
              <template v-if="isProcessingOutboundDispatch">
                分码合计已满足出库数量。提交后将扣减加工投入料库存并结案本派工单（<strong>无需打码业务核销</strong>）。
              </template>
              <template v-else>
                各明细「本分卷确认」已齐（{{ tocConfirmedCount }}/{{ navItems.length }}）。提交后业务员将结案或退回修改；<strong>提交后本页不可再编辑</strong>，直至业务员退回。
              </template>
            </p>
            <el-input
              v-model="completeNotes"
              type="textarea"
              :rows="2"
              placeholder="现场备注（可选，将随提交一并保存）"
              class="m-mt"
            />
            <el-button
              type="success"
              block
              class="m-mt"
              :loading="completing"
              :disabled="!canOperateDispatch"
              @click="submitWarehouseBizReview"
            >
              {{ isProcessingOutboundDispatch ? '确认分码完成并加工出库' : '确认仓库分卷完成并提交业务审查' }}
            </el-button>
          </template>
          <template v-else-if="!isProcessingOutboundDispatch && allSlittingLinesConfirmed && dispatchRemainingQty > BRANCH_QTY_MATCH_EPS">
            <p class="m-hint m-hint--warn">
              各明细「本分卷确认」已齐，但<strong>全单剩余应打为 {{ dispatchRemainingQtyDisplay }}</strong>，须补齐分码至订单数量后再提交。
            </p>
          </template>
          <template v-else>
            <p class="m-hint">
              请先在<strong>明细目录</strong>逐条完成跑码与「本分卷已确认」，进度 <strong>{{ tocConfirmedCount }}/{{ navItems.length }}</strong>；全部打勾后可提交。
            </p>
          </template>
        </section>

        <section v-if="showPostCompletionAuditCard" class="m-section m-section--post-audit">
          <h3 class="m-h3">业务员 · 现场核销</h3>
          <p class="m-hint">
            仓库已提交完成，请核对现场分卷与库存数据。通过则结案；退回则派工单恢复为进行中，仓库可改正后再次提交。
          </p>
          <el-input
            v-model="postCompletionNotes"
            type="textarea"
            :rows="3"
            placeholder="核销备注（退回时建议填写原因）"
            class="m-mt"
          />
          <div class="m-post-audit-actions m-mt">
            <el-button
              type="danger"
              plain
              block
              :loading="postCompletionLoading"
              @click="submitPostCompletionAudit('reject')"
            >
              退回修改
            </el-button>
            <el-button
              type="success"
              block
              :loading="postCompletionLoading"
              @click="submitPostCompletionAudit('approve')"
            >
              核销通过
            </el-button>
          </div>
        </section>

        <section
          v-if="showRevokePostCompletionCard"
          class="m-section m-section--post-audit-revoke"
        >
          <h3 class="m-h3">业务员 · 取消核销</h3>
          <p class="m-hint">
            若误核销或需重新分码，可取消已通过现场核销；本单将恢复为<strong>进行中</strong>，与桌面端派工单管理一致。
          </p>
          <el-button
            type="warning"
            block
            class="m-mt"
            :loading="postCompletionRevokeLoading"
            :disabled="!canRevokeVerification"
            @click="submitRevokePostCompletionAudit"
          >
            取消核销（恢复进行中）
          </el-button>
        </section>

        <section v-if="!readonly && !warehouseCompletionSubmitted" class="m-section">
          <el-button
            v-if="order.status === 'draft' || order.status === 'assigned'"
            type="warning"
            plain
            block
            :disabled="!canOperateDispatch"
            @click="setStatus('in_progress')"
          >
            开始作业
          </el-button>
        </section>

        <section v-if="viewMode === 'toc'" class="m-section m-section--toc">
          <div class="m-toc-head">
            <h3 class="m-h3 m-h3--row">明细目录（{{ navItems.length }} 条）</h3>
            <div class="m-toc-head__actions">
              <span class="m-toc-progress" title="本分卷已在服务端确认的数量（多人同步）">
                已确认 <strong>{{ tocConfirmedCount }}</strong> / {{ navItems.length }}
              </span>
              <el-button link type="primary" size="small" :loading="loading" @click="refreshDispatchFromServer">
                同步进度
              </el-button>
            </div>
          </div>
          <p class="m-toc-hint muted">
            点一行进入该条；分卷处理完请点明细里「本分卷已确认」回到本页。勾选保存在服务器，其他人进入本单或点「同步进度」即可看到最新勾。
          </p>
          <div class="m-toc-list">
            <button
              v-for="(it, idx) in navItems"
              :key="it.dispatch_order_item_id || idx"
              type="button"
              class="m-toc-row"
              :class="{ 'is-current-line': idx === currentIdx }"
              @click="openItemAt(idx)"
            >
              <span class="m-toc-row__idx">{{ idx + 1 }}</span>
              <div class="m-toc-row__main">
                <div class="m-toc-row__title">
                  <span class="m-toc-row__code">{{ tocRowWhCode(it) }}</span>
                  <span v-if="tocNavItemKickbackPending(it)" class="m-toc-kickback" title="踢回配料申请审核中">审</span>
                  <span v-if="tocNavItemConfirmed(it)" class="m-toc-tick" title="本分卷已确认">✓</span>
                </div>
                <div v-if="tocRowMergedInternalMaterial(it)" class="m-toc-row__mat muted">
                  {{ tocRowMergedInternalMaterial(it) }}
                </div>
                <div v-if="tocRowMaterialLocations(it)" class="m-toc-row__loc muted">
                  位置 {{ tocRowMaterialLocations(it) }}
                </div>
                <div v-if="tocRowMatchingRemarks(it)" class="m-toc-row__notes muted">
                  配料备注 {{ tocRowMatchingRemarks(it) }}
                </div>
                <div v-if="tocRowUsageNotes(it)" class="m-toc-row__notes muted">
                  用料备注 {{ tocRowUsageNotes(it) }}
                </div>
                <div class="m-toc-row__line muted">{{ tocRowDetailLine(it) }}</div>
                <div v-if="tocRowOrderNotesPreview(it)" class="m-toc-row__ord-notes muted">
                  明细备注 {{ tocRowOrderNotesPreview(it) }}
                </div>
              </div>
              <span class="m-toc-row__chev" aria-hidden="true">›</span>
            </button>
          </div>
        </section>

        <section
          v-if="viewMode === 'item'"
          class="m-section m-section--carousel"
          @touchstart.passive="onTouchStart"
          @touchend.passive="onTouchEnd"
        >
          <h3 class="m-h3">订单明细（{{ navItems.length }} 条）</h3>
          <div class="m-step-nav m-step-nav--large">
            <el-button class="m-step-btn" :disabled="currentIdx <= 0" circle @click="prevItem">‹</el-button>
            <div class="m-step-nav__center">
              <div class="m-step-nav__txt-wrap">
                <span class="m-step-nav__txt">第 {{ currentIdx + 1 }} / {{ navItems.length }} 条</span>
                <span v-if="currentKickbackPending" class="m-toc-kickback" title="踢回配料申请审核中">审</span>
              </div>
              <div v-if="current" class="m-step-nav__hint">左右滑动或点箭头切换</div>
            </div>
            <el-button class="m-step-btn" :disabled="currentIdx >= navItems.length - 1" circle @click="nextItem">›</el-button>
          </div>
        </section>

        <div v-if="viewMode === 'item' && current" class="m-toc-inline-nav">
          <el-button type="primary" link block @click="goToToc">↑ 返回明细目录</el-button>
        </div>

        <template v-if="current && viewMode === 'item'">
          <section class="m-section">
            <h3 class="m-h3">派工明细</h3>
            <div class="m-kv">
              <span class="k">{{ mergeGroupHasMultipleOrders ? '订单号（合并）' : '订单号' }}</span>
              <span class="v">{{ mergedOrderNumbersLabel }}</span>
            </div>
            <div class="m-kv"><span class="k">客人</span><span class="v">{{ current.customer_name || '—' }}</span></div>
            <div class="m-kv"><span class="k">季节</span><span class="v">{{ currentSeasonDisplay }}</span></div>
            <div class="m-kv"><span class="k">订单类型</span><span class="v">{{ currentOrderTypeDisplay }}</span></div>
            <div v-if="isProcessingOutboundDispatch" class="m-kv">
              <span class="k">订单需求</span>
              <span class="v">
                {{ orderRequiredQtyLabel }}
                <span v-if="orderRequiredOrderCount" class="m-muted">（{{ orderRequiredOrderCount }} 单）</span>
              </span>
            </div>
            <div class="m-kv">
              <span class="k">{{ isProcessingOutboundDispatch ? '出库目标' : '合并数量' }}</span>
              <span class="v strong">{{ mergedQtyLabel }}</span>
            </div>
            <div class="m-kv"><span class="k">客户材料名</span><span class="v">{{ oi.client_material_name || '—' }}</span></div>
            <div class="m-kv"><span class="k">内部材料</span><span class="v">{{ mergedInternalMaterial }}</span></div>
            <div class="m-kv">
              <span class="k">内部颜色</span>
              <span class="v">{{ mergedInternalColor }}</span>
            </div>
            <div class="m-kv"><span class="k">规格</span><span class="v">{{ mergedSpec }}</span></div>
            <div v-if="hasAnyOrderLineNotes" class="m-kv m-kv--block m-order-line-notes">
              <span class="k">明细备注（订单）</span>
              <div class="v">
                <p v-if="trimOrderLineNote(oi.item_notes)" class="m-note-block">
                  <span class="m-note-tag">一</span>{{ trimOrderLineNote(oi.item_notes) }}
                </p>
                <p v-if="trimOrderLineNote(oi.item_notes_2)" class="m-note-block">
                  <span class="m-note-tag">二</span>{{ trimOrderLineNote(oi.item_notes_2) }}
                </p>
                <p v-if="trimOrderLineNote(oi.item_notes_3)" class="m-note-block">
                  <span class="m-note-tag">三</span>{{ trimOrderLineNote(oi.item_notes_3) }}
                </p>
              </div>
            </div>
            <div class="m-kv"><span class="k">仓库料号</span><span class="v strong">{{ whCode }}</span></div>
            <div class="m-kv"><span class="k">系统批号</span><span class="v">{{ batchNo }}</span></div>
            <div v-if="mergeGroupHasMultipleOrders && mergedBatchLinesAgg.length > 1" class="m-kv m-kv--block">
              <span class="k">分批计划 / 现场实际用量（合并）</span>
              <p class="m-muted-tip m-batch-tip">
                以下为合并组内各订单配料分摊合计。请按现场跑码填写各料号「实际用量」；保存时合计须严格等于目标
                （已分码则取分码扣减合计，否则取合并数量；允许误差 {{ BRANCH_QTY_MATCH_EPS }}）。
              </p>
              <div class="v m-batch-lines">
                <div v-for="row in mergedBatchLinesAgg" :key="row.key" class="m-batch-line">
                  <div class="m-batch-line__meta">
                    <span>{{ row.material_batch_number || '未命名批次' }}</span>
                    <span v-if="row.warehouse_manual_code" class="muted">（{{ row.warehouse_manual_code }}）</span>
                    <span class="muted">计划 {{ fmtNumMaybe1(row.allocatedSum) }}</span>
                    <el-tag v-if="row.line_notes === 'AUTO_COMPLEMENT'" size="small" type="warning">补数批</el-tag>
                  </div>
                  <div v-if="!readonly" class="m-batch-line__input">
                    <span class="muted">实际</span>
                    <el-input
                      v-model="mergeBatchActualInputs[row.key]"
                      inputmode="decimal"
                      placeholder="必填"
                      class="m-batch-actual-input"
                      clearable
                    />
                  </div>
                  <div v-else class="m-batch-line__readonly">
                    <strong>实际 {{ fmtNumMaybe1(row.actualSum) }}</strong>
                  </div>
                </div>
              </div>
              <div v-if="!readonly" class="m-batch-actions">
                <span class="m-muted-tip">目标 {{ fmtNumMaybe1(mergeActualTargetQty) }} · 当前合计 {{ fmtNumMaybe1(mergeActualInputSum) }}</span>
                <el-button
                  type="primary"
                  plain
                  size="small"
                  :loading="savingMergedBatchActual"
                  :disabled="!canOperateDispatch"
                  @click="saveMergedBatchActualUsage"
                >
                  保存实际用量
                </el-button>
              </div>
            </div>
            <div v-else-if="dispatchMatchingLines.length > 1" class="m-kv m-kv--block">
              <span class="k">分批计划 / 现场实际用量</span>
              <p class="m-muted-tip m-batch-tip">
                计划为配料分摊；「实际」由下方各分码「本卷用料」汇总回写。折补另选整扣料号，不计入本卷用料合计。
              </p>
              <div class="v m-batch-lines">
                <div
                  v-for="(ln, idx) in dispatchMatchingLines"
                  :key="ln.matching_line_id || idx"
                  class="m-batch-line"
                >
                  <div class="m-batch-line__meta">
                    <span>{{ ln.material_batch_number || '未命名批次' }}</span>
                    <span v-if="ln.warehouse_manual_code" class="muted">（{{ ln.warehouse_manual_code }}）</span>
                    <span class="muted">计划 {{ fmtNumMaybe1(ln.allocated_quantity) }}</span>
                    <el-tag v-if="ln.line_notes === 'AUTO_COMPLEMENT'" size="small" type="warning">补数批</el-tag>
                  </div>
                  <div v-if="!readonly" class="m-batch-line__input">
                    <span class="muted">实际</span>
                    <el-input
                      v-model="batchActualInputs[ln.matching_line_id]"
                      inputmode="decimal"
                      placeholder="与计划相同可不改"
                      class="m-batch-actual-input"
                      clearable
                    />
                  </div>
                  <div v-else class="m-batch-line__readonly">
                    <strong>实际 {{ fmtNumMaybe1(ln.effective_actual_used != null ? ln.effective_actual_used : ln.allocated_quantity) }}</strong>
                  </div>
                </div>
              </div>
              <div v-if="!readonly" class="m-batch-actions">
                <el-checkbox v-model="balanceComplementOnSave" size="small">按订单量平衡补数批</el-checkbox>
                <el-button
                  type="primary"
                  plain
                  size="small"
                  :loading="savingBatchActual"
                  :disabled="!canOperateDispatch || !oi.order_item_id"
                  @click="saveBatchActualUsage"
                >
                  保存实际用量
                </el-button>
              </div>
            </div>
            <div v-if="locLines.length" class="m-locs">
              <div v-for="(loc, i) in locLines" :key="i" class="m-loc-line">{{ loc }}</div>
            </div>
            <p v-else class="muted">{{ locLinesEmptyHint }}</p>
            <div v-if="materialUsageNotesDisplay" class="m-kv m-kv--block">
              <span class="k">用料备注</span>
              <span class="v">{{ materialUsageNotesDisplay }}</span>
            </div>
            <div v-if="matchingRemarksDisplay" class="m-kv m-kv--block">
              <span class="k">配料备注</span>
              <span class="v">{{ matchingRemarksDisplay }}</span>
            </div>
            <el-alert
              v-if="allowMultiBatchSameRoll"
              class="m-mt m-mix-roll-alert"
              type="warning"
              show-icon
              :closable="false"
              title="特别提醒：允许不同料号分同一卷"
            >
              <p v-if="multiBatchSameRollNotes">{{ multiBatchSameRollNotes }}</p>
              <p v-else class="muted">请按派工说明操作混卷；每卷须填写各料号用量且合计等于本卷数量。</p>
            </el-alert>
            <el-alert
              v-else-if="assignedBatchOptions.length > 1"
              class="m-mt m-mix-roll-alert"
              type="info"
              show-icon
              :closable="false"
              title="多料号：每卷只选一个料号"
            >
              本明细有多个派单料号。未开启「同卷混料」时，不同料号请分成不同卷。
            </el-alert>
          </section>

          <section v-if="materialReuseHint" class="m-section m-section--tip">
            <div class="m-tip-icon">ℹ️</div>
            <p class="m-tip-text">{{ materialReuseHint }}</p>
          </section>

          <section class="m-section m-section--kickback">
            <h3 class="m-h3">踢回配料</h3>
            <template v-if="currentKickbackPending">
              <el-alert type="info" show-icon :closable="false">
                <template #title>申请审核中</template>
                <template v-if="mergeGroupHasMultipleOrders">
                  请等待业务在「派工单管理」中确认；通过后将本合并组内各订单行一并移出派工单并退回配料。
                </template>
                <template v-else>
                  请等待业务在「派工单管理」中确认；通过后将本行移出派工单并退回配料。
                </template>
                <p v-if="currentKickbackPending.requested_at" class="muted sm m-kickback-pending-meta">
                  提交时间 {{ formatDt(currentKickbackPending.requested_at) }}
                  <template v-if="currentKickbackPending.requester_name">
                    · {{ currentKickbackPending.requester_name }}
                  </template>
                </p>
                <p v-if="currentKickbackPending.reason" class="m-kickback-reason-preview">
                  <span class="muted">原因：</span>{{ currentKickbackPending.reason }}
                </p>
              </el-alert>
            </template>
            <template v-else-if="readonly">
              <p class="muted sm">派工单已结束或已取消，无法申请踢回。</p>
            </template>
            <template v-else-if="!canOperateAssignedDispatch">
              <p class="muted sm">您无权对此派工单提交踢回申请。</p>
            </template>
            <template v-else>
              <p class="m-hint">
                <template v-if="mergeGroupHasMultipleOrders">
                  本行为合并展示（{{ mergeGroupMembers(current).length }} 条订单）。若无法继续完成，可申请整组踢回；业务同意后组内各订单行均移出本派工单并退回配料。
                </template>
                <template v-else>
                  若本行明细有误、找不到材料等无法继续完成，可申请将该行从本派工单移除；业务同意后订单行退回配料模块待重新处理。
                </template>
              </p>
              <el-button type="warning" plain block @click="openKickbackDialog">
                {{ mergeGroupHasMultipleOrders ? '申请踢回本合并行（整组）' : '申请踢回本行' }}
              </el-button>
            </template>
          </section>

          <section class="m-section">
            <h3 class="m-h3">分码 / 分卷明细</h3>
            <p class="m-hint">
              每条一卷/一件：标签编号由系统自动生成（派工单号+两位序号），请按该编号手写或打印贴标；并填写数量（1 位小数）。
              <template v-if="branchInputInYards">订单单位为米，现场请直接填写<strong>跑码机读数（码）</strong>，系统会自动换算保存。</template>
              保存后系统生成本支「包装编号」（派工单号 + 序列号），称重与外包装尺寸请在
              <router-link :to="mPath(`dispatch/${route.params.id}/packaging`)" class="m-inline-link">包装称重页</router-link>
              按编号填写。
            </p>

            <div v-for="(row, ri) in branchRows" :key="row.branch_record_id || `tmp-${ri}`" class="m-branch-card">
              <div class="m-branch-card__head">
                <span>第 {{ ri + 1 }} 条</span>
                <div class="m-branch-card__actions">
                  <el-button type="primary" link size="small" @click="openBranchLabelPrintForRow(row)">打印标签</el-button>
                  <el-button v-if="!readonly && branchRows.length > 1" type="danger" link size="small" @click="removeRow(ri)">删除</el-button>
                </div>
              </div>
              <p class="m-branch-label-code">
                实际编号 {{ displayedBranchLabel(row, order?.dispatch_order_no, ri) }}
                <span v-if="!row.branch_label" class="muted">（保存后生效）</span>
              </p>
              <p v-if="row.branch_pack_code" class="m-branch-pack-code">包装编号 {{ row.branch_pack_code }}</p>
              <p v-else-if="row.branch_record_id" class="muted sm m-branch-pack-pending">保存本页分码后将生成包装编号</p>
              <div class="m-pack-field">
                <label class="m-lbl">数量 ({{ branchQtyUnitLabel }})</label>
                <el-input
                  v-model="row.quantityInput"
                  :disabled="readonly"
                  inputmode="decimal"
                  placeholder="如 10 或 10.5"
                  class="m-fw"
                  clearable
                  @change="onBranchQtyChanged(row)"
                />
              </div>
              <div v-if="assignedBatchOptions.length" class="m-pack-field m-roll-batch">
                <label class="m-lbl">本卷用料</label>
                <template v-if="assignedBatchOptions.length === 1">
                  <p class="m-roll-batch__single">
                    {{ assignedBatchOptions[0].label }}
                    <span class="muted">（本明细唯一料号）</span>
                  </p>
                </template>
                <template v-else-if="!allowMultiBatchSameRoll">
                  <el-select
                    v-model="row.rollBatchId"
                    :disabled="readonly"
                    placeholder="点选本卷料号"
                    class="m-fw"
                    clearable
                    @change="() => onRollBatchChanged(row)"
                  >
                    <el-option
                      v-for="opt in assignedBatchOptions"
                      :key="opt.material_batch_id"
                      :label="opt.label"
                      :value="opt.material_batch_id"
                    />
                  </el-select>
                </template>
                <template v-else>
                  <div
                    v-for="(u, ui) in row.batchUsages"
                    :key="ui"
                    class="m-roll-usage-line"
                  >
                    <el-select
                      v-model="u.material_batch_id"
                      :disabled="readonly"
                      placeholder="料号"
                      class="m-roll-usage-line__batch"
                      clearable
                    >
                      <el-option
                        v-for="opt in assignedBatchOptions"
                        :key="opt.material_batch_id"
                        :label="opt.label"
                        :value="opt.material_batch_id"
                      />
                    </el-select>
                    <el-input
                      v-model="u.quantityInput"
                      :disabled="readonly"
                      inputmode="decimal"
                      placeholder="用量"
                      class="m-roll-usage-line__qty"
                      clearable
                    />
                    <el-button
                      v-if="!readonly && row.batchUsages.length > 1"
                      text
                      type="danger"
                      @click="row.batchUsages.splice(ui, 1)"
                    >
                      删
                    </el-button>
                  </div>
                  <el-button
                    v-if="!readonly"
                    type="primary"
                    plain
                    size="small"
                    class="m-mt"
                    @click="addRollUsageLine(row)"
                  >
                    ＋ 增加料号用量
                  </el-button>
                </template>
              </div>
              <el-input v-model="row.branch_material_notes" :disabled="readonly" type="textarea" :rows="2" placeholder="分支说明 / 条码等" />
              <div class="m-fold-row">
                <el-checkbox
                  v-model="row.foldCompensation"
                  :disabled="readonly"
                  @change="(v) => onFoldCompensationChange(row, v)"
                >折补（瑕疵等额外扣库存）</el-checkbox>
              </div>
              <div v-if="row.foldCompensation" class="m-pack-field">
                <label class="m-lbl">折补数量 ({{ branchQtyUnitLabel }})</label>
                <el-input
                  v-model="row.foldCompensationQtyInput"
                  :disabled="readonly"
                  inputmode="decimal"
                  placeholder="如 0.5"
                  class="m-fw"
                  clearable
                />
              </div>
              <div v-if="row.foldCompensation && assignedBatchOptions.length" class="m-pack-field">
                <label class="m-lbl">折补整扣料号</label>
                <el-select
                  v-model="row.foldCompensationBatchId"
                  :disabled="readonly"
                  placeholder="点选整扣料号"
                  class="m-fw"
                  clearable
                >
                  <el-option
                    v-for="opt in assignedBatchOptions"
                    :key="'fold-' + opt.material_batch_id"
                    :label="opt.label"
                    :value="opt.material_batch_id"
                  />
                </el-select>
              </div>
              <p
                v-if="branchPackMeasuredLine(row)"
                class="muted sm m-branch-pack-measured"
              >
                {{ branchPackMeasuredLine(row) }}
              </p>
            </div>

            <div v-if="!readonly" ref="addBranchFooterRef" class="m-add-branch">
              <el-button type="primary" plain block @click="addRow">＋ 增加一条</el-button>
            </div>

            <div class="m-recon" :class="{ warn: !qtyMatch }">
              <div class="m-recon__row">
                <span>{{ isProcessingOutboundDispatch ? '本分码合计（对出库目标）' : '本分码合计（对合并数量）' }}</span><span class="num">{{ fmtNumMaybe1(branchSumDisplay) }} {{ branchQtyUnitLabel }}</span>
              </div>
              <div class="m-recon__row">
                <span>库存扣减参考</span><span class="num">{{ fmtNumMaybe1(branchStockOutSumDisplay) }} {{ branchQtyUnitLabel }}</span>
              </div>
              <div class="m-recon__row">
                <span>{{ isProcessingOutboundDispatch ? '出库目标' : '合并数量' }}</span><span class="num">{{ mergedQtyShortLabel }}</span>
              </div>
              <div class="m-recon__row strong">
                <span>本行剩余应打</span><span class="num">{{ fmtNumMaybe1(lineRemainingQtyDisplay) }} {{ branchQtyUnitLabel }}</span>
              </div>
              <div class="m-recon__row strong">
                <span>全单剩余应打</span><span class="num">{{ fmtNumMaybe1(dispatchRemainingQtyDisplay) }} {{ branchQtyUnitLabel }}</span>
              </div>
              <p v-if="!qtyMatch && branchRows.length" class="m-warn-text">
                {{ isProcessingOutboundDispatch ? '本行合计与出库目标不一致，完成工单前请核对。' : '本行合计与合并数量不一致，完成工单前请核对或强制完成。' }}
              </p>
              <p v-if="branchStockOutSum > branchSum + 0.0001" class="m-muted-tip">
                「库存扣减参考」= 各分码数量 + 折补数量之和，用于账面扣减参考；与订单对单仍以第一行为准。
              </p>
            </div>

            <el-button v-if="!readonly" type="primary" block :loading="savingBranches" :disabled="!canOperateDispatch" @click="saveBranches">保存本分码明细</el-button>
            <el-button
              v-if="showSplitByActualBranches"
              type="warning"
              plain
              block
              class="m-mt"
              :loading="splitByActualLoading"
              :disabled="!canSplitByActualBranches"
              @click="handleSplitByActualBranches"
            >
              按现场分码反写拆单（余量回配料）
            </el-button>
            <p v-if="showSplitByActualBranches && !canSplitByActualBranches && splitByActualDisabledHint" class="muted sm m-mt">
              {{ splitByActualDisabledHint }}
            </p>
            <el-button
              v-if="!readonly && navItems.length > 0"
              type="success"
              plain
              block
              class="m-mt"
              :disabled="!canConfirmSlittingLineDone"
              @click="confirmSlittingLineDone"
            >
              ✓ 本分卷已确认（返回目录）
            </el-button>
            <p v-if="!readonly && navItems.length > 0 && !canConfirmSlittingLineDone" class="muted sm m-mt">
              请先保存分码明细，并使「本行剩余应打」为 0{{ branchQtyUnitLabel ? ` ${branchQtyUnitLabel}` : '' }}（分码合计须等于{{ isProcessingOutboundDispatch ? '出库目标' : '合并数量' }}）后再点确认；
              若现场实码不足订单量，可先点「按现场分码反写拆单」将余量退回配料。
            </p>
          </section>

          <section class="m-section">
            <h3 class="m-h3">派工包装要求</h3>
            <p class="m-hint">由桌面端派工人填写；仓库按下列提示执行。</p>
            <p v-if="showWarehousePackInputs" class="m-hint">
              跑码后再集中称重/量尺寸时，可打开
              <router-link :to="mPath(`dispatch/${route.params.id}/packaging`)" class="m-inline-link">包装称重页</router-link>
              单独填写，与下方输入框数据相同。
            </p>
            <div class="m-kv"><span class="k">袋型</span><span class="v">{{ packagingTypeDisplay }}</span></div>
            <div class="m-kv"><span class="k">需要称重</span><span class="v">{{ currentPackagingRequireWeigh ? '是' : '否' }}</span></div>
            <div class="m-kv"><span class="k">需要量体积</span><span class="v">{{ currentPackagingRequireVolume ? '是' : '否' }}</span></div>
          </section>

          <section v-if="!readonly && showWarehousePackInputs" class="m-section">
            <div class="m-split-h">
              <h3 class="m-h3">整单实测（兼容）</h3>
            </div>
            <p class="m-hint">
              派工要求为按支称重/量体积时，请以
              <router-link :to="mPath(`dispatch/${route.params.id}/packaging`)" class="m-inline-link">包装称重页</router-link>
              按系统生成的「包装编号」逐支填写。下方整单字段仅作旧数据兼容，新流程不必填。
            </p>
            <div v-if="currentPackagingRequireWeigh" class="m-pack-measures">
              <div class="m-pack-field">
                <label class="m-lbl">重量 (kg)</label>
                <el-input
                  v-model="warehousePackedForm.warehouse_packed_weight_str"
                  inputmode="decimal"
                  placeholder="如 12.5"
                  class="m-fw"
                  clearable
                />
              </div>
            </div>
            <div v-if="currentPackagingRequireVolume" class="m-pack-dims m-pack-measures">
              <div class="m-pack-field">
                <label class="m-lbl">长 (cm)</label>
                <el-input
                  v-model="warehousePackedForm.warehouse_packed_length_str"
                  inputmode="numeric"
                  placeholder="整数"
                  class="m-fw"
                  clearable
                />
              </div>
              <div class="m-pack-field">
                <label class="m-lbl">宽 (cm)</label>
                <el-input
                  v-model="warehousePackedForm.warehouse_packed_width_str"
                  inputmode="numeric"
                  placeholder="整数"
                  class="m-fw"
                  clearable
                />
              </div>
              <div class="m-pack-field">
                <label class="m-lbl">高 (cm)</label>
                <el-input
                  v-model="warehousePackedForm.warehouse_packed_height_str"
                  inputmode="numeric"
                  placeholder="整数"
                  class="m-fw"
                  clearable
                />
              </div>
            </div>

            <el-button type="primary" plain block :loading="savingPackaging" :disabled="!canOperateDispatch" @click="saveWarehousePacked">保存实测数据</el-button>
          </section>

          <section v-if="hasInventoryReconcile" class="m-section">
            <h3 class="m-h3">库存核对 · 报溢 / 报损</h3>
            <p class="m-hint">
              下方「参考账面」= 当前库位系统物理数量减去<strong>{{ isMultiBatchDispatch ? '本库位分摊应扣' : '本页分码扣减合计（含折补）' }}</strong>，为报溢/报损前的参考值；请对照现场实跑数量判断报溢、报损或点「确认实跑与账面相符」。
            </p>
            <div v-if="invSlotOptions.length" class="m-pack-field m-mb">
              <label class="m-lbl">操作库位</label>
              <el-select
                v-model="selectedInvLocationId"
                :disabled="readonly"
                filterable
                placeholder="选择库位"
                class="m-fw"
                style="width: 100%"
              >
                <el-option
                  v-for="opt in invSlotOptions"
                  :key="opt.location_id"
                  :label="opt.label"
                  :value="opt.location_id"
                />
              </el-select>
            </div>
            <div class="m-kv">
              <span class="k">当前系统物理</span><span class="v strong">{{ fmtNumMaybe1(qtyPhysicalBase) }} {{ branchQtyUnitLabel }}</span>
            </div>
            <div class="m-kv">
              <span class="k">{{ reconcileDeductionLabel }}</span><span class="v">{{ fmtNumMaybe1(reconcileDeductionDisplay) }} {{ branchQtyUnitLabel }}</span>
            </div>
            <div v-if="locationBatchReconcileRows.length > 1" class="m-loc-batch-breakdown m-mb-sm">
              <p class="m-lbl muted">本库位各料号</p>
              <div v-for="row in locationBatchReconcileRows" :key="row.material_batch_id" class="m-kv m-kv--sub">
                <span class="k">{{ row.warehouse_manual_code }}</span>
                <span class="v">物理 {{ fmtNumMaybe1(qtyInDisplayUnit(row.physical_quantity)) }} · 分摊 {{ fmtNumMaybe1(qtyInDisplayUnit(row.allocated_quantity)) }} {{ branchQtyUnitLabel }}</span>
              </div>
            </div>
            <div class="m-kv">
              <span class="k">参考账面（报溢报损前）</span><span class="v strong">{{ fmtNumMaybe1(referencePhysicalBeforeAdjustDisplay) }} {{ branchQtyUnitLabel }}</span>
            </div>
            <p v-if="selectedInvSlot?.physical_run_verified_at" class="m-run-tag muted">
              本库位料号已在系统标「实跑核对」：{{ formatDt(selectedInvSlot.physical_run_verified_at) }}
              <template v-if="selectedInvSlot.physical_run_verified_by_name">
                · {{ selectedInvSlot.physical_run_verified_by_name }}
              </template>
            </p>

            <el-checkbox v-if="!readonly" v-model="inventoryUnverified" class="m-inv-unverified">
              本行库存尚未核验（未跑完、账面未对完等）
            </el-checkbox>

            <template v-if="!readonly">
              <el-button
                type="success"
                plain
                block
                class="m-mb-sm"
                :loading="confirmingPhysicalRun"
                :disabled="!canAuditReview || !selectedInvSlot || inventoryUnverified"
                @click="confirmPhysicalRunMatch"
              >
                确认实跑与账面相符
              </el-button>
              <p v-if="inventoryUnverified" class="m-hint">已勾选「尚未核验」时不能打实跑确认；请先取消勾选或完成核对。</p>
            </template>

            <div v-if="canStockAdjust" class="m-qty-actions m-mt">
              <el-button type="success" plain size="small" :disabled="readonly || !canOperateDispatch" @click="openQtyDialog('overflow')">报溢入库</el-button>
              <el-button type="warning" plain size="small" :disabled="readonly || !canOperateDispatch" @click="openQtyDialog('loss')">报损出库</el-button>
            </div>
            <p v-if="!readonly && !canStockAdjust && hasInventoryReconcile" class="m-hint">
              报溢/报损需管理员配置交易类型与单位；不影响参考账面与实跑确认。
            </p>
          </section>

          <div class="m-toc-inline-nav m-toc-inline-nav--footer">
            <el-button type="primary" link block @click="goToToc">↑ 返回明细目录</el-button>
          </div>
        </template>

        <el-dialog
          v-model="kickbackDialogVisible"
          title="申请踢回配料"
          width="90%"
          destroy-on-close
          @closed="resetKickbackDialog"
        >
          <p class="m-hint m-mb-sm">
            <template v-if="mergeGroupHasMultipleOrders">
              请说明无法完成的原因（至少 5 个字）。提交后需业务同意，将整组
              {{ mergeGroupMembers(current).length }} 条订单明细一并移出派工单并退回配料。
            </template>
            <template v-else>
              请说明无法完成的原因（至少 5 个字）。提交后需业务在派工单管理中同意，本行才会移出并退回配料。
            </template>
          </p>
          <el-input
            v-model="kickbackReason"
            type="textarea"
            :rows="5"
            maxlength="4000"
            show-word-limit
            placeholder="例如：仓库找不到对应批次，与配料单不符…"
          />
          <template #footer>
            <el-button @click="kickbackDialogVisible = false">取消</el-button>
            <el-button type="warning" :loading="kickbackSubmitting" @click="submitKickbackRequest">提交申请</el-button>
          </template>
        </el-dialog>

        <el-dialog v-model="qtyDialogVisible" :title="qtyDialogTitle" width="90%" @closed="resetQtyForm">
          <div v-if="selectedInvSlot?.batches?.length > 1" class="m-pack-field m-mb">
            <label class="m-lbl">操作料号</label>
            <el-select
              v-model="selectedAdjustBatchId"
              filterable
              placeholder="选择料号"
              class="m-fw"
              style="width: 100%"
            >
              <el-option
                v-for="b in selectedInvSlot.batches"
                :key="b.material_batch_id"
                :label="(b.warehouse_manual_code || '料号').trim() + ' · 物理 ' + fmtNumMaybe1(qtyInDisplayUnit(b.physical_quantity))"
                :value="b.material_batch_id"
              />
            </el-select>
          </div>
          <p class="m-qty-cur">当前库位物理数量 <strong>{{ fmtPhysicalOne(qtyDialogPhysicalBase) }}</strong></p>
          <p class="m-qty-tip">
            {{
              qtyMode === 'overflow'
                ? '填「增加数量」按该数额报溢；只填「实际数量」则自动按与当前的差额报溢。两者都填时以「增加数量」为准。'
                : '填「减少数量」按该数额报损；只填「实际数量」则自动按与当前的差额报损。两者都填时以「减少数量」为准。'
            }}
          </p>
          <el-form label-position="top" class="m-qty-form">
            <template v-if="qtyMode === 'overflow'">
              <el-form-item label="增加数量">
                <div class="m-qty-row">
                  <el-input
                    v-model="qtyForm.deltaInput"
                    inputmode="decimal"
                    placeholder="留空可仅填下行"
                    clearable
                    class="m-qty-plain"
                    @blur="onBlurQtyField('delta')"
                  />
                  <div class="m-qty-step">
                    <el-button size="small" @click="bumpQtyField('delta', 0.1)">+0.1</el-button>
                    <el-button size="small" @click="bumpQtyField('delta', -0.1)">−0.1</el-button>
                  </div>
                </div>
              </el-form-item>
              <el-form-item label="实际数量（报溢后）">
                <div class="m-qty-row">
                  <el-input
                    v-model="qtyForm.actualInput"
                    inputmode="decimal"
                    placeholder="留空可仅填上行"
                    clearable
                    class="m-qty-plain"
                    @blur="onBlurQtyField('actual')"
                  />
                  <div class="m-qty-step">
                    <el-button size="small" @click="bumpQtyField('actual', 0.1)">+0.1</el-button>
                    <el-button size="small" @click="bumpQtyField('actual', -0.1)">−0.1</el-button>
                  </div>
                </div>
              </el-form-item>
            </template>
            <template v-else>
              <el-form-item label="减少数量">
                <div class="m-qty-row">
                  <el-input
                    v-model="qtyForm.deltaInput"
                    inputmode="decimal"
                    placeholder="留空可仅填下行"
                    clearable
                    class="m-qty-plain"
                    @blur="onBlurQtyField('delta')"
                  />
                  <div class="m-qty-step">
                    <el-button size="small" @click="bumpQtyField('delta', 0.1)">+0.1</el-button>
                    <el-button size="small" @click="bumpQtyField('delta', -0.1)">−0.1</el-button>
                  </div>
                </div>
              </el-form-item>
              <el-form-item label="实际数量（报损后）">
                <div class="m-qty-row">
                  <el-input
                    v-model="qtyForm.actualInput"
                    inputmode="decimal"
                    placeholder="留空可仅填上行"
                    clearable
                    class="m-qty-plain"
                    @blur="onBlurQtyField('actual')"
                  />
                  <div class="m-qty-step">
                    <el-button size="small" @click="bumpQtyField('actual', 0.1)">+0.1</el-button>
                    <el-button size="small" @click="bumpQtyField('actual', -0.1)">−0.1</el-button>
                  </div>
                </div>
              </el-form-item>
            </template>
            <el-form-item label="备注">
              <el-input v-model="qtyForm.notes" type="textarea" :rows="2" placeholder="选填" />
            </el-form-item>
          </el-form>
          <template #footer>
            <el-button @click="qtyDialogVisible = false">取消</el-button>
            <el-button type="primary" :loading="qtySubmitting" :disabled="!canOperateDispatch" @click="submitQtyTransaction">提交</el-button>
          </template>
        </el-dialog>
      </template>
    </div>

    <PrintPreview
      v-model="branchLabelPrintVisible"
      dialog-title="分码标签打印"
      template-type="dispatch_order"
      :record-id="branchLabelPrintRecordId"
      :dispatch-order-item-id="branchLabelPrintDispatchOrderItemId"
      :branch-record-id="branchLabelPrintBranchRecordId"
      initial-scenario-key="dispatch_branch_label"
      :show-scenario-selector="false"
      :show-template-selector="false"
      :enable-legacy-table-fallback="false"
      @printed="onBranchLabelPrinted"
    />

    <DispatchOutboundLocationDialog
      v-model="outboundLocDialogVisible"
      :payload="outboundLocPayload"
      compact
      @confirm="onOutboundLocationPicked"
    />
  </div>
</template>

<script setup>
defineOptions({ name: 'MobileDispatchDetail' })
import { ref, computed, watch, onMounted, onActivated, reactive, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useMobileWorkbench } from '@/composables/useMobileWorkbench'

const { mPath } = useMobileWorkbench()
import { ElMessage, ElMessageBox } from 'element-plus'
import {
  getDispatchOrder,
  getDispatchOrderItemBranchRecords,
  saveDispatchOrderItemBranchRecords,
  confirmDispatchItemPhysicalRun,
  warehouseCompleteDispatchOrder,
  updateDispatchOrderStatus,
  saveDispatchWarehousePackaging,
  printDispatchOrder,
  setDispatchOrderItemMobileSlittingConfirmed,
  postCompletionAuditDispatchOrder,
  revokePostCompletionAuditDispatchOrder,
  splitDispatchOrderItemByActualBranches
} from '@/api/dispatchOrders'
import { createDispatchOrderItemKickbackRequest } from '@/api/dispatchKickback'
import PrintPreview from '@/components/PrintPreview.vue'
import DispatchOutboundLocationDialog from '@/components/DispatchOutboundLocationDialog.vue'
import { patchOrderItemMatchingLinesActualUsed } from '@/api/orderMaterialMatchings'
import { createInventoryTransaction } from '@/api/inventoryTransactions'
import {
  interpretInventoryTransactionResponse,
  notifyInventoryTransactionResult
} from '@/utils/inventoryTransactionResponse'
import { getUnits } from '@/api/units'
import { getInventoryTransactionTypes } from '@/api/inventoryTransactionTypes'
import { useUserStore } from '@/store/user'
import { hasPermission } from '@/utils/permission'
import { classifyDispatchDue } from '@/utils/dispatchDueUrgency'
import { hasTaskReturnQuery, navigateTaskReturnIfPresent } from '@/utils/taskReturnNavigation'
import { displayedBranchLabel } from '@/utils/dispatchBranchLabel'
import {
  dispatchBranchInputUsesYards,
  metersToYards,
  yardsToMeters
} from '@/utils/fabricInventoryQty'

/** 分码对账容忍：与后端 QTY_EPS 一致（一位小数跑码 vs 更细目标） */
const BRANCH_QTY_MATCH_EPS = 0.1

const route = useRoute()
const router = useRouter()
const userStore = useUserStore()

const backButtonLabel = computed(() =>
  hasTaskReturnQuery(route.query) ? '← 返回任务列表' : '← 返回列表'
)

const DISPATCH_DETAIL_ROUTE = 'MobileDispatchDetail'

function isDispatchDetailRoute() {
  return route.name === DISPATCH_DETAIL_ROUTE
}
const canOperateDispatch = computed(() => hasPermission(userStore.user, 'orders.dispatch.edit'))
const canAuditReview = computed(() => hasPermission(userStore.user, 'orders.dispatch.review'))
const canRevokeVerification = computed(() =>
  hasPermission(userStore.user, 'orders.dispatch.revoke_verification')
)
/** 与后端一致：派工相关权限或指派给自己的派工单可操作（含提交踢回申请） */
const canOperateAssignedDispatch = computed(() => {
  const u = userStore.user
  const o = order.value
  if (!u || !o) return false
  if (Number(u.is_superuser) || Number(u.is_admin)) return true
  if (
    hasPermission(u, 'orders.dispatch.view') ||
    hasPermission(u, 'orders.dispatch.edit') ||
    hasPermission(u, 'orders.dispatch.review') ||
    hasPermission(u, 'operations.execute')
  ) {
    return true
  }
  const aid = o.assigned_user_id
  if (aid == null || aid === '') return false
  return Number(aid) === Number(u.user_id)
})
const loading = ref(true)
const savingBranches = ref(false)
const savingPackaging = ref(false)
const savingBatchActual = ref(false)
const batchActualInputs = ref({})
const savingMergedBatchActual = ref(false)
const mergeBatchActualInputs = ref({})
const balanceComplementOnSave = ref(true)
const completing = ref(false)
const outboundLocDialogVisible = ref(false)
const outboundLocPayload = ref(null)
const outboundLocPendingForce = ref(false)
const postCompletionNotes = ref('')
const postCompletionLoading = ref(false)
const postCompletionRevokeLoading = ref(false)
const order = ref(null)
const dispatchHeroDue = computed(() => classifyDispatchDue(order.value || {}))
const currentIdx = ref(0)
const branchRows = ref([])
const loadedBranchSnapshot = ref('')
const addBranchFooterRef = ref(null)
const confirmingPhysicalRun = ref(false)
const completeNotes = ref('')

const overflowTypeId = ref(null)
const lossTypeId = ref(null)
const yardUnitId = ref(null)
const selectedInvLocationId = ref(null)
const selectedAdjustBatchId = ref(null)
const inventoryUnverified = ref(false)

const qtyDialogVisible = ref(false)
const qtyMode = ref('overflow')
const qtySubmitting = ref(false)
const kickbackDialogVisible = ref(false)
const kickbackReason = ref('')
const kickbackSubmitting = ref(false)
const splitByActualLoading = ref(false)
const qtyForm = reactive({
  deltaInput: '',
  actualInput: '',
  notes: ''
})

const warehousePackedForm = reactive({
  warehouse_packed_weight_str: '',
  warehouse_packed_length_str: '',
  warehouse_packed_width_str: '',
  warehouse_packed_height_str: ''
})

const branchLabelPrintVisible = ref(false)
/** 当前要打印的那一条分码（保存后才有 id），预览里只含一条记录 */
const branchLabelPrintBranchRecordId = ref(null)

const branchLabelPrintRecordId = computed(() => {
  const fromOrder = order.value?.dispatch_order_id
  if (fromOrder != null && fromOrder !== '') return fromOrder
  const id = Number(route.params.id)
  return Number.isFinite(id) ? id : null
})

async function openBranchLabelPrintForRow(row) {
  const id = branchLabelPrintRecordId.value
  if (id == null || !Number.isFinite(Number(id))) {
    ElMessage.warning('无法打印，请稍后重试')
    return
  }
  if (branchLabelPrintDispatchOrderItemId.value == null || branchLabelPrintDispatchOrderItemId.value === '') {
    ElMessage.warning('请先选择一条派工明细')
    return
  }
  if (!row?.branch_record_id) {
    ElMessage.warning('请先保存本分码明细，再打印该条标签')
    return
  }
  branchLabelPrintBranchRecordId.value = row.branch_record_id
  await nextTick()
  branchLabelPrintVisible.value = true
}

watch(branchLabelPrintVisible, (visible) => {
  if (!visible) branchLabelPrintBranchRecordId.value = null
})

async function onBranchLabelPrinted() {
  const id = branchLabelPrintRecordId.value
  if (id == null) return
  try {
    await printDispatchOrder(id)
    await loadOrder()
  } catch (e) {
    ElMessage.error(e.response?.data?.message || e.message || '记录打印次数失败')
  }
}

const items = computed(() => {
  const raw = order.value?.items
  return Array.isArray(raw) ? raw : []
})

/** 桌面「合并数量」同一 merge_group_key：导航/目录只保留一条（代表行取组内第一条） */
function mergeGroupMembers(it) {
  if (!it) return []
  const mk = it.merge_group_key
  if (mk == null || String(mk).trim() === '') return [it]
  const key = String(mk).trim()
  return items.value.filter((x) => x.merge_group_key != null && String(x.merge_group_key).trim() === key)
}

function mergeGroupMembersSorted(it) {
  return mergeGroupMembers(it)
    .slice()
    .sort((a, b) => Number(a.dispatch_order_item_id) - Number(b.dispatch_order_item_id))
}

/** 当前明细上已保存分码数量之和（来自详情接口的 branch_records，未再请求分码 API） */
function branchRecordsQtySumFromPayload(m) {
  const rows = m?.branch_records
  if (!Array.isArray(rows)) return 0
  return rows.reduce((s, r) => s + Number(r.quantity || 0), 0)
}

/**
 * 合并出库数量：分码 API 只挂在单条 dispatch_order_item 上。
 * 目录「代表行」是 items 顺序里同 merge_group_key 的第一个，未必与保存分码的那条 id 一致，
 * 若始终用 current.dispatch_order_item_id 拉分码，会出现全单已分齐仍提示本行约 N 码未分。
 * 规则：组内已有分码合计最大的明细（并列取 id 较小）；全无分码则取组内最小派工明细 id，便于新单统一落点。
 */
function getBranchOwnerDispatchOrderItemId(it) {
  if (!it?.dispatch_order_item_id) return null
  const members = mergeGroupMembersSorted(it)
  if (members.length <= 1) return Number(it.dispatch_order_item_id)
  let best = members[0]
  let bestSum = branchRecordsQtySumFromPayload(best)
  for (const m of members.slice(1)) {
    const s = branchRecordsQtySumFromPayload(m)
    if (s > bestSum + 0.0001) {
      best = m
      bestSum = s
    }
  }
  return Number(best.dispatch_order_item_id)
}

const navItems = computed(() => {
  const raw = items.value
  if (!raw.length) return []
  const out = []
  const seen = new Set()
  for (const it of raw) {
    const mk = it.merge_group_key
    if (mk != null && String(mk).trim() !== '') {
      const key = String(mk).trim()
      if (seen.has(key)) continue
      seen.add(key)
    }
    out.push(it)
  }
  return out
})

/** 明细目录 vs 单条编辑 */
const viewMode = ref('toc')

/** 旧版仅写本机 localStorage 的确认；与服务器合并显示，并在加载后自动迁到服务端 */
const SLITTING_LS_KEY = 'm_dispatch_slitting_confirmed_v1'
const slittingDisplayTick = ref(0)

function getSlittingStorageMap() {
  try {
    const raw = localStorage.getItem(SLITTING_LS_KEY)
    if (!raw) return {}
    const o = JSON.parse(raw)
    return typeof o === 'object' && o != null ? o : {}
  } catch {
    return {}
  }
}

function getLegacyConfirmedIdsForDispatch(dispatchOrderId) {
  if (dispatchOrderId == null) return new Set()
  const m = getSlittingStorageMap()
  const arr = m[String(dispatchOrderId)]
  if (!Array.isArray(arr)) return new Set()
  return new Set(arr.map(Number).filter((n) => Number.isFinite(n)))
}

function removeLegacySlittingId(dispatchOrderId, dispatchOrderItemId) {
  const m = getSlittingStorageMap()
  const key = String(dispatchOrderId)
  const arr = m[key]
  if (!Array.isArray(arr)) return
  const next = arr.filter((x) => Number(x) !== Number(dispatchOrderItemId))
  if (next.length) m[key] = next
  else delete m[key]
  try {
    localStorage.setItem(SLITTING_LS_KEY, JSON.stringify(m))
  } catch {
    /* ignore quota */
  }
  slittingDisplayTick.value += 1
}

function isSlittingConfirmedForItem(it) {
  slittingDisplayTick.value
  const at = it?.mobile_slitting_confirmed_at
  if (at && String(at).trim()) return true
  const oid = order.value?.dispatch_order_id
  const doi = it?.dispatch_order_item_id
  if (oid == null || doi == null) return false
  return getLegacyConfirmedIdsForDispatch(oid).has(Number(doi))
}

function tocNavItemConfirmed(it) {
  const members = mergeGroupMembers(it)
  return members.length > 0 && members.every((m) => isSlittingConfirmedForItem(m))
}

/** 合并组内任一派工明细有待审批的踢回申请 */
function tocNavItemKickbackPending(it) {
  if (!it) return false
  return mergeGroupMembers(it).some((m) => m && m.kickback_request_pending)
}

const tocConfirmedCount = computed(() => navItems.value.filter((it) => tocNavItemConfirmed(it)).length)

/** 与桌面/目录勾一致：流程已完成但本分卷确认未齐时顶栏不显示「已完成」 */
const heroStatusDisplay = computed(() => {
  const o = order.value
  if (!o) return { text: '', tag: 'info' }
  const t = navItems.value.length || Number(o.mobile_slitting_total_lines ?? o.item_count ?? 0)
  const c = tocConfirmedCount.value
  if (o.status === 'completed' && t > 0 && c < t) {
    return { text: '进行中', tag: 'warning' }
  }
  return { text: statusText(o.status), tag: statusTag(o.status) }
})

function mergeSlittingPatchIntoOrder(patch) {
  const id = patch?.dispatch_order_item_id
  if (id == null || !order.value?.items) return
  const list = order.value.items.map((row) =>
    Number(row.dispatch_order_item_id) === Number(id) ? { ...row, ...patch } : row
  )
  order.value = { ...order.value, items: list }
}

/** 进入页面后：把旧版 localStorage 里的勾选写入服务器，并清掉已迁移项 */
async function migrateLegacySlittingToServer() {
  const oid = order.value?.dispatch_order_id
  if (oid == null || !items.value.length) return
  const legacy = getLegacyConfirmedIdsForDispatch(oid)
  if (!legacy.size) return
  let migrated = 0
  for (const it of items.value) {
    const doi = it.dispatch_order_item_id
    if (doi == null) continue
    if (!legacy.has(Number(doi))) continue
    if (it.mobile_slitting_confirmed_at) {
      removeLegacySlittingId(oid, doi)
      continue
    }
    try {
      const res = await setDispatchOrderItemMobileSlittingConfirmed(doi, { confirmed: true })
      if (res.success && res.data) {
        mergeSlittingPatchIntoOrder(res.data)
        removeLegacySlittingId(oid, doi)
        migrated += 1
      }
    } catch {
      /* 保留本地记录，下次联网再同步 */
    }
  }
  slittingDisplayTick.value += 1
  if (migrated > 0) {
    ElMessage.success(`已将 ${migrated} 条本机保存的「分卷确认」同步到服务器`)
  }
}

const qtySummary = computed(() => order.value?.dispatch_quantity_summary || null)

const headerCustomer = computed(() => {
  const fi = items.value.find((i) => i.customer_name)
  return fi?.customer_name || '—'
})

const orderSeasonTypesHero = computed(() => {
  const o = order.value
  if (!o) return ''
  const s = o.seasons_display
  const t = o.order_types_display
  const parts = []
  if (s) parts.push(`季节 ${s}`)
  if (t) parts.push(`订单类型 ${t}`)
  return parts.join(' · ')
})

function formatSeasonFromOi(oi) {
  if (!oi || typeof oi !== 'object') return ''
  const name = oi.season_name
  const year = oi.season_year
  if (!name && (year === null || year === undefined || String(year).trim() === '')) return ''
  return [name, year].filter((x) => x != null && String(x).trim() !== '').join(' ')
}

const currentSeasonDisplay = computed(() => formatSeasonFromOi(oi.value) || '—')

const currentOrderTypeDisplay = computed(() => {
  const t = oi.value?.order_type_name
  return (t && String(t).trim()) || '—'
})

const current = computed(() => navItems.value[currentIdx.value] || null)

/** 当前合并组内任一派工明细有待审批的踢回申请（接口挂在各 dispatch item 上） */
const currentKickbackPending = computed(() => {
  const cur = current.value
  if (!cur) return null
  for (const m of mergeGroupMembers(cur)) {
    const p = m.kickback_request_pending
    if (p && typeof p === 'object') return p
  }
  return null
})

const mergeGroupHasMultipleOrders = computed(() => mergeGroupMembers(current.value).length > 1)

/** 合并组内全部订单号（去重后顿号拼接）；未合并即为当前行订单号 */
const mergedOrderNumbersLabel = computed(() => {
  const cur = current.value
  if (!cur) return '—'
  const members = mergeGroupMembers(cur)
  const nos = [...new Set(members.map((m) => m.order_no).filter(Boolean))]
  if (!nos.length) return cur.order_no || '—'
  return nos.join('、')
})

/** 当前轮播条派工明细，用于分码标签只打「本条」 */
const branchLabelPrintDispatchOrderItemId = computed(() => {
  const cur = current.value
  if (!cur) return null
  return getBranchOwnerDispatchOrderItemId(cur)
})

const materialUsageNotesDisplay = computed(() => String(current.value?.material_usage_notes || '').trim())

const matchingRemarksDisplay = computed(() => {
  const oi = current.value?.order_item || {}
  return String(oi.material_matching?.matching_remarks || current.value?.matching_remarks || '').trim()
})

const packagingTypeDisplay = computed(() => {
  const c = current.value
  const o = order.value
  const t = c?.packaging_type ?? o?.packaging_type
  if (t === 'transparent_bag') return '透明袋'
  if (t === 'woven_bag') return '蛇皮袋'
  return '未指定'
})

const currentPackagingRequireWeigh = computed(() => {
  const c = current.value
  const o = order.value
  return !!(c?.packaging_require_weigh ?? o?.packaging_require_weigh)
})

const currentPackagingRequireVolume = computed(() => {
  const c = current.value
  const o = order.value
  return !!(c?.packaging_require_volume ?? o?.packaging_require_volume)
})

const showWarehousePackInputs = computed(() => {
  if (!order.value) return false
  const list = order.value.items || []
  if (list.some((it) => it.packaging_require_weigh || it.packaging_require_volume)) return true
  return !!(order.value.packaging_require_weigh || order.value.packaging_require_volume)
})

const oi = computed(() => current.value?.order_item || {})

function trimOrderLineNote(v) {
  const s = String(v ?? '').trim()
  return s || ''
}

const hasAnyOrderLineNotes = computed(() => {
  const o = oi.value
  return !!(
    trimOrderLineNote(o.item_notes) ||
    trimOrderLineNote(o.item_notes_2) ||
    trimOrderLineNote(o.item_notes_3)
  )
})

const postCompletionAuditStatus = computed(() => order.value?.post_completion_audit_status || 'na')

const isProcessingOutboundDispatch = computed(
  () =>
    (order.value?.dispatch_purpose || 'delivery') === 'processing_outbound' ||
    !!order.value?.is_processing_outbound
)

/**
 * 现场编辑锁定：以「仓库端已提交完成」（warehouse_completed_at）为准，不以 status===completed 一刀切。
 * 否则若订单被误标为已完成、或分卷未齐时顶栏显示「进行中」，却仍会因 completed 而整单只读。
 */
const warehouseCompletionSubmitted = computed(() => !!order.value?.warehouse_completed_at)

const readonly = computed(() => {
  const st = order.value?.status
  if (st === 'cancelled') return true
  if (warehouseCompletionSubmitted.value) return true
  return false
})

const allSlittingLinesConfirmed = computed(() => {
  if (!navItems.value.length) return false
  return navItems.value.every((it) => tocNavItemConfirmed(it))
})

const showWarehouseBizSubmitCard = computed(() => {
  const o = order.value
  if (!o || o.status === 'cancelled') return false
  if (o.warehouse_completed_at) return false
  // 未记仓库完成前均可出现（含 status 被误标 completed、仍可在此提交进入待核销）
  return true
})

const showPostCompletionAuditCard = computed(() => {
  if (isProcessingOutboundDispatch.value) return false
  if (!order.value) return false
  if (!order.value.warehouse_completed_at) return false
  if (order.value.status !== 'completed') return false
  if (postCompletionAuditStatus.value !== 'pending') return false
  return canAuditReview.value
})

const showRevokePostCompletionCard = computed(() => {
  if (isProcessingOutboundDispatch.value) return false
  if (!order.value) return false
  if (order.value.status !== 'completed') return false
  if (postCompletionAuditStatus.value !== 'approved') return false
  return canRevokeVerification.value
})

const heroBizAuditHint = computed(() => {
  const o = order.value
  if (!o) return ''
  if (isProcessingOutboundDispatch.value) {
    if (o.warehouse_completed_at || o.status === 'completed') {
      return '加工出库分码已完成，本单无需打码业务核销。'
    }
    return ''
  }
  const p = postCompletionAuditStatus.value
  // 核销提示仅在仓库已提交完工后出现（避免仅凭 status 误判）
  if (!o.warehouse_completed_at && p !== 'approved') {
    return ''
  }
  if (p === 'pending') {
    if (canAuditReview.value) {
      return '待您现场核销（见下方「业务员 · 现场核销」）。'
    }
    return '已提交业务员核销，移动端已锁定；业务员退回后可继续修改。'
  }
  if (p === 'approved') {
    if (canRevokeVerification.value) {
      return '业务员已核销通过，本单结案。如需撤销请见下方「取消核销」。'
    }
    return '业务员已核销通过，本单结案。'
  }
  return ''
})

const orderUnit = computed(() => oi.value.unit_name || '')
const orderUnitCode = computed(() => oi.value.unit_code || '')
const branchInputInYards = computed(() =>
  dispatchBranchInputUsesYards(orderUnit.value, orderUnitCode.value)
)
const branchQtyUnitLabel = computed(() => (branchInputInYards.value ? '码' : orderUnit.value || '单位'))

function qtyInDisplayUnit(qtyInOrderUnit) {
  if (branchInputInYards.value) return metersToYards(qtyInOrderUnit)
  return qtyInOrderUnit
}

/** 移动端仅展示合并数量；未配置合并时合并数量等于订单行数量。
 * 加工出库：合并数量字段承载出库/分码目标（processing_target_quantity）。
 */
function dispatchItemMergedQty(it) {
  if (!it) return 0
  const pt = it.processing_target_quantity
  if (pt != null && pt !== '') return Number(pt) || 0
  const mq = it.merged_quantity
  if (mq != null && mq !== '') return Number(mq) || 0
  const ol = it.order_line_quantity
  if (ol != null && ol !== '') return Number(ol) || 0
  const rowOi = it.order_item || {}
  return Number(rowOi.quantity) || 0
}

const orderRequiredOrderCount = computed(() =>
  Number(order.value?.order_required_order_count || current.value?.order_required_order_count || 0) || 0
)

const orderRequiredQty = computed(() => {
  const fromOrder = order.value?.order_required_quantity
  if (fromOrder != null && fromOrder !== '') return Number(fromOrder) || 0
  const fromItem = current.value?.order_required_quantity ?? current.value?.order_line_quantity
  if (fromItem != null && fromItem !== '') return Number(fromItem) || 0
  return 0
})

const orderRequiredQtyLabel = computed(() => {
  const q = fmtNumMaybe1(orderRequiredQty.value)
  if (branchInputInYards.value) {
    return `${q} ${orderUnit.value || '米'}（${fmtNumMaybe1(metersToYards(orderRequiredQty.value))} 码）`
  }
  return `${q} ${orderUnit.value}`
})

const mergedQty = computed(() => dispatchItemMergedQty(current.value))

const mergedQtyLabel = computed(() => {
  const q = fmtNumMaybe1(mergedQty.value)
  if (branchInputInYards.value) {
    return `${q} ${orderUnit.value || '米'}（${fmtNumMaybe1(metersToYards(mergedQty.value))} 码）`
  }
  return `${q} ${orderUnit.value}`
})

const mergedQtyShortLabel = computed(() => {
  if (branchInputInYards.value) {
    return `${fmtNumMaybe1(metersToYards(mergedQty.value))} 码（${fmtNumMaybe1(mergedQty.value)} ${orderUnit.value || '米'}）`
  }
  return `${fmtNumMaybe1(mergedQty.value)} ${orderUnit.value}`
})

const branchStockOutSum = computed(() =>
  branchRows.value.reduce((s, r) => {
    const q = parseBranchQtyInOrderUnit(r.quantityInput)
    const fold = r.foldCompensation ? parseBranchQtyInOrderUnit(r.foldCompensationQtyInput) : 0
    return s + q + (fold > 0 ? fold : 0)
  }, 0)
)

const branchStockOutSumDisplay = computed(() => qtyInDisplayUnit(branchStockOutSum.value))

const qtyDialogTitle = computed(() => (qtyMode.value === 'overflow' ? '报溢入库' : '报损出库'))

const internalColorLine = computed(() => {
  const cn = oi.value.internal_color_cn || ''
  const en = oi.value.internal_color_en || ''
  if (!cn && !en) return '—'
  return [cn && `中 ${cn}`, en && `英 ${en}`].filter(Boolean).join(' · ')
})

/** 订单行与配料字段一致时只显示一处；不一致时保留两侧并标注来源 */
function mergeOrderMatchingKv(orderVal, matchVal) {
  const o = orderVal != null && String(orderVal).trim() !== '' && String(orderVal).trim() !== '—' ? String(orderVal).trim() : ''
  const m = matchVal != null && String(matchVal).trim() !== '' && String(matchVal).trim() !== '—' ? String(matchVal).trim() : ''
  if (!o && !m) return '—'
  if (o === m) return o
  if (!o) return m
  if (!m) return o
  return `${o}（订单） / ${m}（配料）`
}

const mm = computed(() => oi.value.material_matching || {})
const allowMultiBatchSameRoll = computed(() => !!order.value?.allow_multi_batch_same_roll)
const multiBatchSameRollNotes = computed(() =>
  String(order.value?.multi_batch_same_roll_notes || '').trim()
)
/** 本明细派单已分配料号（下拉点选；优先仓库料号，无则标明系统号勿当仓库号） */
const assignedBatchOptions = computed(() => {
  const opts = []
  const seen = new Set()
  const labelOf = (wh, sys, id) => {
    const w = String(wh || '').trim()
    const s = String(sys || '').trim()
    if (w) return w
    if (s) return `未分配仓库号·${s}`
    return `料号#${id}`
  }
  const lines = Array.isArray(mm.value?.matching_lines) ? mm.value.matching_lines : []
  for (const ln of lines) {
    const id = Number(ln?.material_batch_id)
    if (!id || seen.has(id)) continue
    seen.add(id)
    opts.push({
      material_batch_id: id,
      label: labelOf(ln.warehouse_manual_code, ln.material_batch_number, id)
    })
  }
  const headId = Number(mm.value?.material_batch_id)
  if (headId && !seen.has(headId)) {
    seen.add(headId)
    opts.push({
      material_batch_id: headId,
      label: labelOf(mm.value.warehouse_manual_code, mm.value.material_batch_number, headId)
    })
  }
  return opts
})
const dispatchMatchingLines = computed(() => {
  const lines = Array.isArray(mm.value?.matching_lines) ? mm.value.matching_lines : []
  return lines
    .filter((ln) => ln?.material_batch_id && Number(ln?.allocated_quantity || 0) > 0)
    .slice()
    .sort((a, b) => (Number(a?.line_no) || 0) - (Number(b?.line_no) || 0))
})

/** 单条配料行用于展示的「实际」数量（与只读区逻辑一致） */
function matchingLineDisplayActual(ln) {
  if (!ln) return 0
  if (ln.effective_actual_used != null && ln.effective_actual_used !== '') {
    const n = Number(ln.effective_actual_used)
    return Number.isFinite(n) ? n : 0
  }
  if (ln.actual_used_quantity != null && ln.actual_used_quantity !== '') {
    const n = Number(ln.actual_used_quantity)
    return Number.isFinite(n) ? n : 0
  }
  const a = Number(ln.allocated_quantity || 0)
  return Number.isFinite(a) ? a : 0
}

/** API 返回的库存行（按 batch × 库位）；兼容 location_id / storage_location_id */
function normalizeInventoryLocRow(l) {
  if (!l || typeof l !== 'object') return null
  const location_id = l.location_id != null ? l.location_id : l.storage_location_id
  if (location_id == null) return null
  return { ...l, location_id }
}

const rawInventoryLocations = computed(() => {
  const locs = current.value?.inventory_locations || []
  if (!Array.isArray(locs)) return []
  return locs.map(normalizeInventoryLocRow).filter(Boolean)
})

/** 按库位汇总物理量（多批分摊同库位时合并展示） */
const aggregatedInvSlots = computed(() => {
  const map = new Map()
  for (const row of rawInventoryLocations.value) {
    const lid = Number(row.location_id)
    if (!lid) continue
    let slot = map.get(lid)
    if (!slot) {
      slot = {
        location_id: lid,
        location_code: row.location_code || '',
        location_name: row.location_name || '',
        physical_quantity: 0,
        batches: [],
        physical_run_verified_at: null,
        physical_run_verified_by_name: null
      }
      map.set(lid, slot)
    }
    const phy = Number(row.physical_quantity) || 0
    slot.physical_quantity += phy
    slot.batches.push({ ...row, physical_quantity: phy })
    if (row.physical_run_verified_at) {
      slot.physical_run_verified_at = row.physical_run_verified_at
      slot.physical_run_verified_by_name = row.physical_run_verified_by_name || null
    }
  }
  return Array.from(map.values())
})

const isMultiBatchDispatch = computed(() => dispatchMatchingLines.value.length > 1)

const selectedLocationBatchIds = computed(() => {
  const set = new Set()
  for (const b of selectedInvSlot.value?.batches || []) {
    if (b?.material_batch_id) set.add(Number(b.material_batch_id))
  }
  return set
})

/** 本库位应扣：多批分摊时只计该库位上的分摊批；单批仍用整单分码合计 */
const locationScopedDeduction = computed(() => {
  if (!isMultiBatchDispatch.value || !selectedLocationBatchIds.value.size) {
    return branchStockOutSum.value
  }
  let sum = 0
  for (const ln of dispatchMatchingLines.value) {
    const bid = Number(ln.material_batch_id)
    if (!selectedLocationBatchIds.value.has(bid)) continue
    sum += matchingLineDisplayActual(ln)
  }
  return sum
})

const locationScopedDeductionDisplay = computed(() => qtyInDisplayUnit(locationScopedDeduction.value))

const reconcileDeductionLabel = computed(() =>
  isMultiBatchDispatch.value && selectedLocationBatchIds.value.size
    ? '本库位应扣（分摊）'
    : '本分码扣减（含折补）'
)

const reconcileDeductionDisplay = computed(() =>
  isMultiBatchDispatch.value && selectedLocationBatchIds.value.size
    ? locationScopedDeductionDisplay.value
    : branchStockOutSumDisplay.value
)

/** 报溢报损前：库位物理合计 − 本库位应扣（多批分摊按库位口径） */
const referencePhysicalBeforeAdjust = computed(() => {
  const physical = qtyInDisplayUnit(qtyPhysicalBase.value)
  const deduct = reconcileDeductionDisplay.value
  return Math.round((physical - deduct) * 10) / 10
})

const referencePhysicalBeforeAdjustDisplay = computed(() => referencePhysicalBeforeAdjust.value)

/** 多批同库位：各料号物理 / 分摊对照 */
const locationBatchReconcileRows = computed(() => {
  const slot = selectedInvSlot.value
  if (!slot?.batches?.length) return []
  const batchIds = selectedLocationBatchIds.value
  const allocByBatch = new Map()
  for (const ln of dispatchMatchingLines.value) {
    const bid = Number(ln.material_batch_id)
    if (!batchIds.has(bid)) continue
    allocByBatch.set(bid, (allocByBatch.get(bid) || 0) + matchingLineDisplayActual(ln))
  }
  return slot.batches.map((b) => {
    const bid = Number(b.material_batch_id)
    return {
      material_batch_id: bid,
      warehouse_manual_code: (b.warehouse_manual_code || '').trim() || '—',
      physical_quantity: Number(b.physical_quantity) || 0,
      allocated_quantity: allocByBatch.get(bid) || 0
    }
  })
})

const selectedAdjustBatchPhysical = computed(() => {
  const bid = selectedAdjustBatchId.value
  if (!bid) return qtyPhysicalBase.value
  const row = (selectedInvSlot.value?.batches || []).find(
    (b) => Number(b.material_batch_id) === Number(bid)
  )
  return row ? Number(row.physical_quantity) || 0 : qtyPhysicalBase.value
})

const invSlotOptions = computed(() => {
  return aggregatedInvSlots.value.map((slot) => {
    const whs = [
      ...new Set(
        (slot.batches || [])
          .map((b) => (b.warehouse_manual_code || '').trim())
          .filter(Boolean)
      )
    ]
    const locLabel = (slot.location_name || slot.location_code || '库位').trim()
    return {
      location_id: slot.location_id,
      label: whs.length ? `${locLabel} · 料号 ${whs.join('、')}` : locLabel
    }
  })
})

const selectedInvSlot = computed(() => {
  const slots = aggregatedInvSlots.value
  if (!slots.length) return null
  const lid = selectedInvLocationId.value ?? slots[0].location_id
  return slots.find((s) => Number(s.location_id) === Number(lid)) || slots[0]
})

const qtyPhysicalBase = computed(() => Number(selectedInvSlot.value?.physical_quantity) || 0)

const qtyDialogPhysicalBase = computed(() => {
  const batches = selectedInvSlot.value?.batches || []
  if (batches.length > 1) return selectedAdjustBatchPhysical.value
  return qtyPhysicalBase.value
})

const dispatchMaterialBatchId = computed(() => {
  const bid = selectedAdjustBatchId.value
  if (bid) return bid
  const batches = selectedInvSlot.value?.batches || []
  if (batches.length === 1 && batches[0]?.material_batch_id) return batches[0].material_batch_id
  return mm.value?.material_batch_id
})

const dispatchMaterialIdentityId = computed(() => {
  const bid = dispatchMaterialBatchId.value
  if (bid && dispatchMatchingLines.value.length) {
    const ln = dispatchMatchingLines.value.find((l) => Number(l.material_batch_id) === Number(bid))
    if (ln?.material_identity_id) return ln.material_identity_id
  }
  return mm.value?.material_identity_id
})

const canStockAdjust = computed(() => {
  return !!(
    overflowTypeId.value &&
    lossTypeId.value &&
    yardUnitId.value &&
    dispatchMaterialBatchId.value &&
    invSlotOptions.value.length
  )
})

/** 有批次且有库位时可做库存核对（不依赖报溢报损类型是否配置） */
const hasInventoryReconcile = computed(
  () => !!(mm.value?.material_batch_id && invSlotOptions.value.length)
)

/** 合并组：按批次（及是否补数批）汇总计划、实际，并保留分摊行引用供写回 */
const mergedBatchLinesAgg = computed(() => {
  const cur = current.value
  if (!cur) return []
  const members = mergeGroupMembers(cur)
  if (members.length <= 1) return []
  const map = new Map()
  for (const m of members) {
    const oiRow = m.order_item
    const oid = oiRow?.order_item_id
    const mmx = oiRow?.material_matching
    const lines = Array.isArray(mmx?.matching_lines) ? mmx.matching_lines : []
    for (const ln of lines) {
      if (!ln.material_batch_id || Number(ln.allocated_quantity || 0) <= 0) continue
      const comp = ln.line_notes === 'AUTO_COMPLEMENT'
      const key = `${ln.material_batch_id}_${comp ? 'c' : 'n'}`
      let slot = map.get(key)
      if (!slot) {
        slot = {
          key,
          material_batch_id: ln.material_batch_id,
          material_batch_number: ln.material_batch_number,
          warehouse_manual_code: ln.warehouse_manual_code,
          line_notes: ln.line_notes,
          allocatedSum: 0,
          actualSum: 0,
          lineRefs: []
        }
        map.set(key, slot)
      }
      slot.allocatedSum += Number(ln.allocated_quantity || 0)
      slot.actualSum += matchingLineDisplayActual(ln)
      if (oid != null && ln.matching_line_id != null) {
        slot.lineRefs.push({
          order_item_id: oid,
          matching_line_id: ln.matching_line_id,
          allocated: Number(ln.allocated_quantity || 0)
        })
      }
    }
  }
  const arr = Array.from(map.values())
  arr.sort((a, b) => {
    const ac = a.line_notes === 'AUTO_COMPLEMENT' ? 1 : 0
    const bc = b.line_notes === 'AUTO_COMPLEMENT' ? 1 : 0
    if (ac !== bc) return ac - bc
    return String(a.material_batch_number || '').localeCompare(String(b.material_batch_number || ''), 'zh-CN')
  })
  return arr
})

/** 合并组实际用量保存目标：有分码则取分码扣减合计，否则取合并数量 */
const mergeActualTargetQty = computed(() => {
  if (branchStockOutSum.value > BRANCH_QTY_MATCH_EPS) return branchStockOutSum.value
  return Number(mergedQty.value) || 0
})

const mergeActualInputSum = computed(() => {
  let s = 0
  for (const row of mergedBatchLinesAgg.value) {
    const v = parseActualInputForBatch(mergeBatchActualInputs.value[row.key])
    if (v != null && Number.isFinite(v)) s += v
  }
  return Math.round(s * 10) / 10
})

const hasComplementLine = computed(() =>
  dispatchMatchingLines.value.some((ln) => ln.line_notes === 'AUTO_COMPLEMENT')
)

function parseActualInputForBatch(s) {
  if (s == null || String(s).trim() === '') return null
  return parseBranchQty(s)
}

function syncBatchActualInputsFromServer() {
  const lines = dispatchMatchingLines.value
  const next = {}
  for (const ln of lines) {
    const id = ln.matching_line_id
    if (id == null) continue
    const hasAct = ln.actual_used_quantity != null && ln.actual_used_quantity !== ''
    const show = hasAct ? Number(ln.actual_used_quantity) : Number(ln.allocated_quantity || 0)
    next[id] = numToStr1(show)
  }
  batchActualInputs.value = next
}

function syncMergeBatchActualInputsFromServer() {
  const next = {}
  for (const row of mergedBatchLinesAgg.value) {
    next[row.key] = numToStr1(row.actualSum)
  }
  mergeBatchActualInputs.value = next
}

watch(dispatchMatchingLines, () => syncBatchActualInputsFromServer(), { deep: true })
watch(mergedBatchLinesAgg, () => syncMergeBatchActualInputsFromServer(), { deep: true })

async function saveBatchActualUsage() {
  if (!canOperateDispatch.value) {
    ElMessage.warning('您没有保存现场用量权限，请联系管理员')
    return
  }
  const oid = oi.value.order_item_id
  if (!oid) return
  const lines = dispatchMatchingLines.value
  if (!lines.length) return
  savingBatchActual.value = true
  try {
    const payload = lines.map((ln) => {
      const id = ln.matching_line_id
      const raw = batchActualInputs.value[id]
      return {
        matching_line_id: id,
        actual_used_quantity: parseActualInputForBatch(raw)
      }
    })
    const res = await patchOrderItemMatchingLinesActualUsed(oid, {
      lines: payload,
      balance_complement: !!(balanceComplementOnSave.value && hasComplementLine.value)
    })
    if (res.success) {
      ElMessage.success(res.message || '已保存')
      await loadOrder()
    } else {
      ElMessage.error(res.message || '保存失败')
    }
  } catch (e) {
    ElMessage.error(e.response?.data?.message || e.message || '保存失败')
  } finally {
    savingBatchActual.value = false
  }
}

async function saveMergedBatchActualUsage() {
  if (!canOperateDispatch.value) {
    ElMessage.warning('您没有保存现场用量权限，请联系管理员')
    return
  }
  const rows = mergedBatchLinesAgg.value
  if (!rows.length) return

  const actualByKey = {}
  let sum = 0
  for (const row of rows) {
    const v = parseActualInputForBatch(mergeBatchActualInputs.value[row.key])
    if (v == null || !Number.isFinite(v) || v < 0) {
      ElMessage.warning('请填写各料号实际用量（须为非负数）')
      return
    }
    actualByKey[row.key] = v
    sum += v
  }
  sum = Math.round(sum * 10) / 10
  const target = Math.round(Number(mergeActualTargetQty.value) * 10) / 10
  if (Math.abs(sum - target) > BRANCH_QTY_MATCH_EPS) {
    ElMessage.warning(
      `各料号实际合计 ${fmtNumMaybe1(sum)} 须等于目标 ${fmtNumMaybe1(target)}（允许误差 ${BRANCH_QTY_MATCH_EPS}）`
    )
    return
  }

  // 按订单行分组写回；同料号跨多行时按计划比例拆分，尾差落在最后一行
  const byOi = new Map()
  for (const row of rows) {
    const total = actualByKey[row.key]
    const refs = row.lineRefs || []
    if (!refs.length) {
      ElMessage.error(`料号 ${row.material_batch_number || row.material_batch_id} 缺少可写回的分摊行`)
      return
    }
    const allocSum = refs.reduce((s, r) => s + (Number(r.allocated) || 0), 0)
    let remain = total
    refs.forEach((ref, i) => {
      const isLast = i === refs.length - 1
      let part
      if (isLast) {
        part = Math.round(remain * 10) / 10
      } else if (allocSum > 1e-9) {
        part = Math.round(total * (Number(ref.allocated) / allocSum) * 10) / 10
        remain = Math.round((remain - part) * 10) / 10
      } else {
        part = Math.round((total / refs.length) * 10) / 10
        remain = Math.round((remain - part) * 10) / 10
      }
      if (!byOi.has(ref.order_item_id)) byOi.set(ref.order_item_id, [])
      byOi.get(ref.order_item_id).push({
        matching_line_id: ref.matching_line_id,
        actual_used_quantity: part
      })
    })
  }

  savingMergedBatchActual.value = true
  try {
    for (const [oid, lines] of byOi.entries()) {
      const res = await patchOrderItemMatchingLinesActualUsed(oid, { lines })
      if (!res.success) {
        ElMessage.error(res.message || `订单行 ${oid} 保存失败`)
        return
      }
    }
    ElMessage.success('已保存合并组实际用量')
    await loadOrder()
  } catch (e) {
    ElMessage.error(e.response?.data?.message || e.message || '保存失败')
  } finally {
    savingMergedBatchActual.value = false
  }
}

const matchingColorLine = computed(() => {
  const cn = mm.value.material_color_name_cn || ''
  const en = mm.value.material_color_name_en || ''
  if (!cn && !en) return '—'
  return [cn && `中 ${cn}`, en && `英 ${en}`].filter(Boolean).join(' · ')
})

const mergedInternalMaterial = computed(() =>
  mergeOrderMatchingKv(oi.value.internal_product_name, mm.value.material_category_name)
)

const mergedInternalColor = computed(() =>
  mergeOrderMatchingKv(internalColorLine.value, matchingColorLine.value)
)

const mergedSpec = computed(() => mergeOrderMatchingKv(oi.value.specification, mm.value.material_specification))

const whCode = computed(() => {
  const c = current.value
  if (!c) return '—'
  // 加工出库：优先派工明细出库料号，避免回落到订单成品配料
  if (isProcessingOutboundDispatch.value) {
    return c.warehouse_manual_code || c.material_code || '—'
  }
  return c.warehouse_manual_code || c.material_code || c.order_item?.material_matching?.warehouse_manual_code || '—'
})

const batchNo = computed(() => {
  const c = current.value
  if (isProcessingOutboundDispatch.value) {
    return c?.material_batch_number || oi.value.material_matching?.material_batch_number || '—'
  }
  const m = oi.value.material_matching
  return m?.material_batch_number || '—'
})

const locLines = computed(() => {
  const c = current.value
  let locs = c?.inventory_locations
  // 加工出库只用派工明细库存；普通派工可回落订单配料库存
  if ((!Array.isArray(locs) || !locs.length) && !isProcessingOutboundDispatch.value) {
    locs = c?.order_item?.material_matching?.inventory_locations
  }
  if (!Array.isArray(locs) || !locs.length) return []
  return locs.map((l) => {
    const code = (l.location_code || '').trim()
    const name = (l.location_name || '').trim()
    let label = code || name || '库位'
    if (code && name && name !== code) label = `${code}(${name})`
    const wh = l.warehouse_manual_code || ''
    const avail = l.available_quantity != null ? l.available_quantity : ''
    const phy = l.physical_quantity != null ? l.physical_quantity : ''
    const availStr = avail !== '' ? `分码前可用 ${avail}` : ''
    const phyStr = phy !== '' ? `物理 ${phy}` : ''
    const run = l.physical_run_verified_at ? '实跑已核对' : ''
    const mid = [label, wh ? `料号 ${wh}` : '', availStr, phyStr, run].filter(Boolean).join(' · ')
    return mid
  })
})

const locLinesEmptyHint = computed(() =>
  isProcessingOutboundDispatch.value
    ? '暂无出库料号库位库存（请确认加工派工已选出库料号）'
    : '暂无库位库存信息（请确认配料已选料号）'
)

function materialGroupKey(item) {
  if (!item) return ''
  const mid = item.order_item?.material_matching?.material_identity_id
  const code = item.warehouse_manual_code || item.material_code || item.order_item?.material_matching?.material_identity_code
  if (mid) return `id:${mid}`
  if (code) return `code:${code}`
  return ''
}

const materialReuseHint = computed(() => {
  const list = navItems.value
  const cur = current.value
  if (!cur || list.length < 2) return ''
  const key = materialGroupKey(cur)
  if (!key) return ''
  const same = list.filter((it) => materialGroupKey(it) === key)
  if (same.length < 2) return ''
  const after = list.slice(currentIdx.value + 1).filter((it) => materialGroupKey(it) === key)
  if (!after.length) return ''
  const parts = after.map((it) => {
    const q = dispatchItemMergedQty(it)
    const u = it.order_item?.unit_name || ''
    return `${fmtNumMaybe1(q)}${u}`
  })
  return `同料号在本派工单后续还有 ${after.length} 条明细，约需：${parts.join('、')}`
})

const branchSum = computed(() =>
  branchRows.value.reduce((s, r) => s + parseBranchQtyInOrderUnit(r.quantityInput), 0)
)

const branchSumDisplay = computed(() => qtyInDisplayUnit(branchSum.value))

/** 本行：合并数量 − 当前正在编辑的分码合计（含未保存）；对账容忍内视为 0 */
const lineRemainingQty = computed(() => {
  const rem = mergedQty.value - branchSum.value
  return Math.abs(rem) <= BRANCH_QTY_MATCH_EPS ? 0 : rem
})
const lineRemainingQtyDisplay = computed(() => qtyInDisplayUnit(lineRemainingQty.value))

const currentIsMergeGroup = computed(() => {
  const it = current.value
  if (!it) return false
  const mk = it.merge_group_key
  if (!mk || !String(mk).trim()) return false
  return mergeGroupMembers(it).length > 1
})

const savedBranchSumForCurrent = computed(() => {
  const cur = current.value
  const id = cur ? getBranchOwnerDispatchOrderItemId(cur) : null
  if (!qtySummary.value?.items || id == null) return null
  const row = qtySummary.value.items.find((x) => Number(x.dispatch_order_item_id) === Number(id))
  if (!row) return null
  const saved = Number(row.branch_sum_quantity)
  return Number.isFinite(saved) ? saved : null
})

const branchesSavedForSplit = computed(() => {
  const saved = savedBranchSumForCurrent.value
  if (saved == null) return false
  return Math.abs(saved - branchSum.value) < 0.02
})

const showSplitByActualBranches = computed(() => {
  if (readonly.value || viewMode.value !== 'item') return false
  if (order.value?.warehouse_completed_at) return false
  if (currentIsMergeGroup.value) return false
  return lineRemainingQty.value > BRANCH_QTY_MATCH_EPS && branchSum.value > BRANCH_QTY_MATCH_EPS
})

const splitByActualDisabledHint = computed(() => {
  if (!canOperateAssignedDispatch.value) return '您无权操作此派工单'
  if (!branchesSavedForSplit.value) return '请先保存本分码明细，再按现场分码反写拆单'
  if (currentIsMergeGroup.value) return '合并数量组内明细请在桌面端处理'
  return ''
})

const canSplitByActualBranches = computed(() => {
  if (!showSplitByActualBranches.value) return false
  if (!canOperateAssignedDispatch.value) return false
  return branchesSavedForSplit.value
})

/**
 * 全单：后端汇总 + 当前行「已保存分码」与「正在编辑」的差额，
 * 这样在改本分码未点保存时，全单剩余也会跟着变。
 *
 * 目录（TOC）下不应用「当前行」的编辑差额：否则 currentIdx 仍指向某条时，
 * 未保存的分码输入会错误牵动顶栏「全单剩余应打」，甚至出现负数（与是否已分卷无关）。
 */
const dispatchRemainingQty = computed(() => {
  const baseRaw = qtySummary.value?.dispatch_remaining_quantity
  if (baseRaw == null || baseRaw === undefined) return 0
  const base = Number(baseRaw)
  if (!Number.isFinite(base)) return 0
  if (viewMode.value !== 'item') return base
  const cur = current.value
  const id = cur ? getBranchOwnerDispatchOrderItemId(cur) : null
  if (!qtySummary.value?.items || id == null) return base
  const row = qtySummary.value.items.find((x) => Number(x.dispatch_order_item_id) === Number(id))
  if (!row) return base
  const saved = Number(row.branch_sum_quantity)
  if (!Number.isFinite(saved)) return base
  const raw = base + saved - branchSum.value
  // 「剩余应打」按业务语义下限为 0；超额时不再显示负数（与接口 dispatch_remaining_quantity 钳制一致）
  // 分码对账容忍内视为已齐
  const clamped = Math.max(0, raw)
  return clamped <= BRANCH_QTY_MATCH_EPS ? 0 : clamped
})

const dispatchRemainingQtyDisplay = computed(() => qtyInDisplayUnit(dispatchRemainingQty.value))

const qtyMatch = computed(() => {
  if (!branchRows.value.length) return true
  return Math.abs(branchSum.value - mergedQty.value) <= BRANCH_QTY_MATCH_EPS
})

/** 本分卷已确认：须本行剩余应打为 0（分码合计与合并数量一致，含对账容忍），且具备派工操作权限（与接口一致） */
const canConfirmSlittingLineDone = computed(() => {
  if (!canOperateAssignedDispatch.value) return false
  return Math.abs(lineRemainingQty.value) <= BRANCH_QTY_MATCH_EPS
})

function parseBranchQty(s) {
  if (s == null || String(s).trim() === '') return 0
  const n = parseFloat(String(s).replace(',', '.'))
  return Number.isNaN(n) ? 0 : Math.round(n * 10) / 10
}

/** 分码输入转为订单单位（米单按码录入时自动换算为米） */
function parseBranchQtyInOrderUnit(s) {
  const raw = parseBranchQty(s)
  if (branchInputInYards.value) return yardsToMeters(raw)
  return raw
}

function branchQtyInputFromOrderUnit(qtyInOrderUnit) {
  if (qtyInOrderUnit == null || qtyInOrderUnit === '') return ''
  if (branchInputInYards.value) return numToStr1(metersToYards(qtyInOrderUnit))
  return numToStr1(qtyInOrderUnit)
}

function fmtNumMaybe1(v) {
  if (v == null || Number.isNaN(Number(v))) return '0'
  const n = Number(v)
  const r = Math.round(n * 10) / 10
  if (Math.abs(r) < 0.0001) return '0'
  return r % 1 === 0 ? String(r) : r.toFixed(1)
}

function tocRowWhCode(it) {
  const oi = it?.order_item || {}
  return it?.warehouse_manual_code || it?.material_code || oi?.material_matching?.warehouse_manual_code || '—'
}

/** 与「派工明细」内「内部材料」同一套合并规则；兼容仅有配料分类或顶层摘要字段的情况（仓管账号） */
function tocRowMergedInternalMaterial(it) {
  const oi = it?.order_item || {}
  const mm = oi.material_matching || {}
  const merged = mergeOrderMatchingKv(oi.internal_product_name, mm.material_category_name)
  if (merged && merged !== '—') return merged
  const top = it?.material_category_name
  if (top != null && String(top).trim()) return String(top).trim()
  const mmc = mm.material_category_name
  if (mmc != null && String(mmc).trim()) return String(mmc).trim()
  const ipn = oi.internal_product_name
  if (ipn != null && String(ipn).trim()) return String(ipn).trim()
  return ''
}

function tocRowMaterialLocations(it) {
  let locs = it?.inventory_locations
  if ((!Array.isArray(locs) || !locs.length) && !isProcessingOutboundDispatch.value) {
    locs = it?.order_item?.material_matching?.inventory_locations
  }
  if (!Array.isArray(locs) || !locs.length) return ''
  const parts = locs.map((l) => {
    const name = (l.location_name || l.location_code || '').trim() || '库位'
    const wh = (l.warehouse_manual_code || '').trim()
    const whOk = wh && wh !== '未分配'
    return whOk ? `${name} · 料号 ${wh}` : name
  })
  return parts.join('； ')
}

function tocRowMatchingRemarks(it) {
  const oi = it?.order_item || {}
  const s = String(oi.material_matching?.matching_remarks || it?.matching_remarks || '').trim()
  return s || ''
}

function tocRowUsageNotes(it) {
  const s = String(it?.material_usage_notes || '').trim()
  return s || ''
}

/** 目录行：订单明细三备注摘要（与桌面订单表「明细备注一/二/三」同源） */
function tocRowOrderNotesPreview(it) {
  const oi = it?.order_item || {}
  const chunks = [
    String(oi.item_notes || '').trim(),
    String(oi.item_notes_2 || '').trim(),
    String(oi.item_notes_3 || '').trim()
  ].filter(Boolean)
  if (!chunks.length) return ''
  const merged = chunks.join(' · ')
  return merged.length > 44 ? `${merged.slice(0, 44)}…` : merged
}

function dispatchMergedQtyLabel(it) {
  const rowOi = it?.order_item || {}
  const q = dispatchItemMergedQty(it)
  const u = rowOi.unit_name || ''
  const uc = rowOi.unit_code || ''
  if (dispatchBranchInputUsesYards(u, uc)) {
    return `${fmtNumMaybe1(q)} ${u || '米'}（${fmtNumMaybe1(metersToYards(q))} 码）`
  }
  return `${fmtNumMaybe1(q)} ${u}`
}

function tocRowDetailLine(it) {
  const oi = it?.order_item || {}
  const cn = oi.internal_color_cn || ''
  const en = oi.internal_color_en || ''
  const color = [cn, en].filter((x) => x && String(x).trim()).join(' / ') || '—'
  const members = mergeGroupMembers(it)
  let ordPart = `订单 ${it?.order_no || '—'}`
  if (members.length > 1) {
    const nos = [...new Set(members.map((m) => m.order_no).filter(Boolean))]
    ordPart =
      nos.length > 1
        ? `${nos.length}单合并（${nos.join('、')}）`
        : `订单 ${nos[0] || it?.order_no || '—'}（${members.length}条合并）`
  }
  return `${color} · ${dispatchMergedQtyLabel(it)} · ${ordPart}`
}

function numToStr1(n) {
  if (n == null || n === '') return ''
  const x = Number(n)
  if (Number.isNaN(x)) return ''
  const r = Math.round(x * 10) / 10
  if (Math.abs(r) < 0.0001) return '0'
  return r % 1 === 0 ? String(r) : r.toFixed(1)
}

function fmtPhysicalOne(v) {
  if (v == null || v === '') return '0'
  const n = Number(v)
  if (!Number.isFinite(n)) return String(v)
  const r = Math.round(n * 10) / 10
  return Number.isInteger(r) ? String(r) : r.toFixed(1).replace(/\.0$/, '')
}

function parseQtyInputToNumber(str) {
  if (str == null || String(str).trim() === '') return null
  const n = parseFloat(String(str).replace(/,/g, '').trim())
  if (!Number.isFinite(n) || n < 0) return null
  return Math.round(n * 10) / 10
}

function formatQtyInputString(n) {
  if (n == null || !Number.isFinite(n) || n < 0) return ''
  const r = Math.round(n * 10) / 10
  if (r === 0) return ''
  return Number.isInteger(r) ? String(r) : String(r)
}

function onBlurQtyField(which) {
  const key = which === 'delta' ? 'deltaInput' : 'actualInput'
  const n = parseQtyInputToNumber(qtyForm[key])
  qtyForm[key] = n == null ? '' : formatQtyInputString(n)
}

function bumpQtyField(which, step) {
  const key = which === 'delta' ? 'deltaInput' : 'actualInput'
  const cur = parseQtyInputToNumber(qtyForm[key])
  const base = cur == null ? 0 : cur
  const next = Math.round((base + step) * 10) / 10
  qtyForm[key] = next <= 0 ? '' : formatQtyInputString(next)
}

async function loadMeta() {
  try {
    const [typesRes, unitsRes] = await Promise.all([
      getInventoryTransactionTypes({ page: 1, per_page: 200 }),
      getUnits({ page: 1, per_page: 200 })
    ])
    const types = (typesRes && typesRes.data) || []
    const overflow = types.find((t) => t.type_code === 'OVERFLOW_IN')
    const loss = types.find((t) => t.type_code === 'LOSS_OUT')
    overflowTypeId.value = overflow?.type_id ?? null
    lossTypeId.value = loss?.type_id ?? null

    const units = (unitsRes && unitsRes.data) || []
    const yard =
      units.find((u) => u.unit_code === 'yd' || u.unit_code === 'YD' || u.unit_code === 'YARD') ||
      units.find((u) => u.unit_name === '码')
    yardUnitId.value = yard?.unit_id ?? null

    if (!overflowTypeId.value || !lossTypeId.value) {
      console.warn('未找到报溢/报损交易类型 OVERFLOW_IN / LOSS_OUT')
    }
    if (!yardUnitId.value) {
      console.warn('未找到默认单位「码」')
    }
  } catch (e) {
    console.error(e)
  }
}

function syncAdjustBatchForSelectedSlot() {
  const batches = selectedInvSlot.value?.batches || []
  if (!batches.length) {
    selectedAdjustBatchId.value = null
    return
  }
  const prev = selectedAdjustBatchId.value
  if (prev && batches.some((b) => Number(b.material_batch_id) === Number(prev))) return
  selectedAdjustBatchId.value = batches[0].material_batch_id
}

function syncInvSelectionFromCurrent() {
  const slots = aggregatedInvSlots.value
  if (!slots.length) {
    selectedInvLocationId.value = null
    selectedAdjustBatchId.value = null
    inventoryUnverified.value = false
    return
  }
  const prev = selectedInvLocationId.value
  const stillValid = prev && slots.some((s) => Number(s.location_id) === Number(prev))
  selectedInvLocationId.value = stillValid ? prev : slots[0].location_id
  syncAdjustBatchForSelectedSlot()
  inventoryUnverified.value = current.value?.inventory_verified === false
}

function resetKickbackDialog() {
  kickbackReason.value = ''
}

function openKickbackDialog() {
  if (readonly.value) return
  if (!canOperateAssignedDispatch.value) {
    ElMessage.warning('无权提交踢回申请')
    return
  }
  kickbackReason.value = ''
  kickbackDialogVisible.value = true
}

async function submitKickbackRequest() {
  const reason = (kickbackReason.value || '').trim()
  if (reason.length < 5) {
    ElMessage.warning('请填写原因（至少 5 个字）')
    return
  }
  const doi = current.value?.dispatch_order_item_id
  if (!doi) {
    ElMessage.warning('缺少派工明细')
    return
  }
  kickbackSubmitting.value = true
  try {
    const res = await createDispatchOrderItemKickbackRequest(doi, { reason })
    if (res.success) {
      ElMessage.success(res.message || '已提交申请')
      kickbackDialogVisible.value = false
      await loadOrder()
    } else {
      ElMessage.error(res.message || '提交失败')
    }
  } catch (e) {
    ElMessage.error(e.response?.data?.message || e.message || '提交失败')
  } finally {
    kickbackSubmitting.value = false
  }
}

async function handleSplitByActualBranches() {
  if (!canSplitByActualBranches.value) {
    ElMessage.warning(splitByActualDisabledHint.value || '当前不可反写拆单')
    return
  }
  const doi = getBranchOwnerDispatchOrderItemId(current.value)
  if (!doi) {
    ElMessage.warning('缺少派工明细')
    return
  }
  const shipQty = branchSum.value
  const remainQty = lineRemainingQty.value
  try {
    await ElMessageBox.confirm(
      `将按已保存分码合计 ${fmtNumMaybe1(qtyInDisplayUnit(shipQty))}${branchQtyUnitLabel.value ? ` ${branchQtyUnitLabel.value}` : ''} 作为本派工先出数量，` +
        `余量约 ${fmtNumMaybe1(qtyInDisplayUnit(remainQty))}${branchQtyUnitLabel.value ? ` ${branchQtyUnitLabel.value}` : ''} 另起订单行回到配料池。` +
        '染费仍保留在本派工行。是否继续？',
      '确认反写拆单',
      { type: 'warning', confirmButtonText: '确认拆单', cancelButtonText: '取消' }
    )
  } catch {
    return
  }
  splitByActualLoading.value = true
  try {
    const res = await splitDispatchOrderItemByActualBranches(doi, {
      actual_ship_quantity: shipQty
    })
    if (res.success) {
      ElMessage.success(res.message || '反写拆单成功')
      if (res.data?.dispatch_quantity_summary) {
        qtySummary.value = res.data.dispatch_quantity_summary
      }
      await loadOrder()
    } else {
      ElMessage.error(res.message || '反写拆单失败')
    }
  } catch (e) {
    ElMessage.error(e.response?.data?.message || e.message || '反写拆单失败')
  } finally {
    splitByActualLoading.value = false
  }
}

function openQtyDialog(mode) {
  if (readonly.value) return
  if (!canStockAdjust.value) {
    ElMessage.warning('缺少报溢/报损类型或单位配置，或本行无库位/批次，请联系管理员')
    return
  }
  if (!selectedInvSlot.value) {
    ElMessage.warning('请先选择操作库位')
    return
  }
  syncAdjustBatchForSelectedSlot()
  qtyMode.value = mode
  qtyForm.deltaInput = ''
  qtyForm.actualInput = ''
  qtyForm.notes = ''
  qtyDialogVisible.value = true
}

function resetQtyForm() {
  qtyForm.deltaInput = ''
  qtyForm.actualInput = ''
  qtyForm.notes = ''
}

function resolveOverflowSubmitQty() {
  const cur = qtyDialogPhysicalBase.value
  const d = parseQtyInputToNumber(qtyForm.deltaInput)
  const a = parseQtyInputToNumber(qtyForm.actualInput)
  if (d != null && d > 0) return d
  if (a != null) {
    const inc = Math.round((a - cur) * 10) / 10
    if (inc <= 0) return { error: '实际数量须大于当前物理数量，差额才会作为报溢量' }
    return inc
  }
  return { error: '请填写「增加数量」或「实际数量」其一' }
}

function resolveLossSubmitQty() {
  const cur = qtyDialogPhysicalBase.value
  const d = parseQtyInputToNumber(qtyForm.deltaInput)
  const a = parseQtyInputToNumber(qtyForm.actualInput)
  if (d != null && d > 0) {
    if (d > cur) return { error: '减少数量不能大于当前物理数量' }
    return d
  }
  if (a != null) {
    if (a >= cur) return { error: '报损后的实际数量须小于当前物理数量' }
    if (a < 0) return { error: '实际数量不能为负' }
    const loss = Math.round((cur - a) * 10) / 10
    if (loss <= 0) return { error: '差额须大于 0 才能报损' }
    return loss
  }
  return { error: '请填写「减少数量」或「实际数量」其一' }
}

async function submitQtyTransaction() {
  if (!canOperateDispatch.value) {
    ElMessage.warning('您没有库存调整权限，请联系管理员')
    return
  }
  const slot = selectedInvSlot.value
  const batchId = dispatchMaterialBatchId.value
  const identityId = dispatchMaterialIdentityId.value
  if (!slot || !batchId) {
    ElMessage.warning('缺少库位或批次信息')
    return
  }
  const typeId = qtyMode.value === 'overflow' ? overflowTypeId.value : lossTypeId.value
  const resolved = qtyMode.value === 'overflow' ? resolveOverflowSubmitQty() : resolveLossSubmitQty()
  if (typeof resolved === 'object' && resolved?.error) {
    ElMessage.warning(resolved.error)
    return
  }
  const q = resolved
  if (!Number.isFinite(q) || q <= 0) {
    ElMessage.warning('请填写有效的数量')
    return
  }
  qtySubmitting.value = true
  try {
    const operator = userStore.user?.username || userStore.user?.name || '用户'
    const timeStr = new Date().toISOString().slice(0, 19).replace('T', ' ')
    const noteParts = [
      `[派工单核对 ${route.params.id}] [操作员：${operator}] [时间：${timeStr}]`,
      qtyForm.notes
    ].filter(Boolean)
    const res = await createInventoryTransaction({
      transaction_type_id: typeId,
      quantity: q,
      unit_id: yardUnitId.value,
      notes: noteParts.join(' '),
      material_identity_id: identityId,
      material_batch_id: batchId,
      storage_location_id: slot.location_id,
      is_adjustment: true,
      related_order_type: 'adjustment'
    })
    const result = interpretInventoryTransactionResponse(res, '已提交审批')
    if (notifyInventoryTransactionResult(result)) {
      qtyDialogVisible.value = false
      if (!result.pendingApproval) await loadOrder(doi)
      await loadBranchesForCurrent()
      if (dispatchMatchingLines.value.length) {
        ElMessage.info('若报溢/报损与各批实际用量有关，请在上方「分批计划 / 现场实际用量」中核对并保存。')
      }
    } else {
      ElMessage.error(result.message || '提交失败')
    }
  } catch (e) {
    ElMessage.error(e.response?.data?.message || e.message || '提交失败')
  } finally {
    qtySubmitting.value = false
  }
}

function formatDt(s) {
  if (!s) return ''
  return String(s).replace('T', ' ').slice(0, 19)
}

function statusText(s) {
  const map = { draft: '草稿', assigned: '已指派', in_progress: '进行中', completed: '已完成', cancelled: '已取消' }
  return map[s] || s
}

function statusTag(s) {
  const map = { draft: 'info', assigned: 'primary', in_progress: 'warning', completed: 'success', cancelled: 'danger' }
  return map[s] || ''
}

function currentBranchSnapshot() {
  const rows = (branchRows.value || []).map((r, i) => {
    let foldQty = null
    if (r.foldCompensation) {
      const raw = String(r.foldCompensationQtyInput ?? '').trim()
      if (raw !== '') foldQty = parseBranchQtyInOrderUnit(r.foldCompensationQtyInput)
    }
    return {
      idx: i,
      branch_record_id: r.branch_record_id || null,
      quantity: parseBranchQtyInOrderUnit(r.quantityInput),
      branch_material_notes: (r.branch_material_notes || '').trim(),
      is_fold_compensation: !!r.foldCompensation,
      fold_compensation_qty: foldQty,
      fold_compensation_batch_id: r.foldCompensationBatchId || null,
      roll_batch_id: r.rollBatchId || null,
      batch_usages: buildBatchUsagesPayload(r)
    }
  })
  return JSON.stringify({ rows, inventory_verified: !inventoryUnverified.value })
}

function markBranchSnapshotSaved() {
  loadedBranchSnapshot.value = currentBranchSnapshot()
}

function hasUnsavedBranchChanges() {
  if (readonly.value) return false
  return currentBranchSnapshot() !== loadedBranchSnapshot.value
}

async function ensureCurrentBranchSavedBeforeLeave() {
  if (!hasUnsavedBranchChanges()) return true
  return saveBranches({ silent: true, toastOnSuccess: false })
}

async function goBack() {
  const ok = await ensureCurrentBranchSavedBeforeLeave()
  if (!ok) return
  if (navigateTaskReturnIfPresent(router, route, { replace: true })) return
  router.push(mPath('dispatch'))
}

function syncLineQuery() {
  if (!order.value) return
  const id = String(route.params.id || '').trim()
  if (!id) return
  const base = { path: mPath(`dispatch/${id}`), query: { ...route.query } }
  if (viewMode.value === 'item' && navItems.value.length > 1) {
    base.query.line = String(currentIdx.value)
    router.replace(base)
  } else {
    delete base.query.line
    router.replace(base)
  }
}

/** 首次进入 / 切换派工单：按 URL ?line= 打开对应条，否则目录页 */
function applyInitialLineFromRoute() {
  const qLine = route.query.line
  if (qLine != null && String(qLine).trim() !== '') {
    const idx = parseInt(String(qLine), 10)
    if (!Number.isNaN(idx) && idx >= 0 && idx < navItems.value.length) {
      currentIdx.value = idx
      viewMode.value = 'item'
      return
    }
  }
  viewMode.value = 'toc'
}

async function goToToc() {
  const ok = await ensureCurrentBranchSavedBeforeLeave()
  if (!ok) return
  viewMode.value = 'toc'
  syncLineQuery()
}

async function openItemAt(idx) {
  if (idx < 0 || idx >= navItems.value.length) return
  if (idx !== currentIdx.value) {
    const ok = await ensureCurrentBranchSavedBeforeLeave()
    if (!ok) return
    currentIdx.value = idx
  }
  viewMode.value = 'item'
  syncLineQuery()
}

async function refreshDispatchFromServer() {
  const prefer = current.value?.dispatch_order_item_id
  await loadOrder(prefer)
  await loadBranchesForCurrent()
}

async function confirmSlittingLineDone() {
  if (!canConfirmSlittingLineDone.value) {
    ElMessage.warning(
      isProcessingOutboundDispatch.value
        ? '请先使「本行剩余应打」为 0（保存分码且合计等于出库目标）后再确认本分卷'
        : '请先使「本行剩余应打」为 0（保存分码且合计等于合并数量）后再确认本分卷'
    )
    return
  }
  const ok = await ensureCurrentBranchSavedBeforeLeave()
  if (!ok) return
  const cur = current.value
  if (!cur) return
  const targets = mergeGroupMembers(cur)
  try {
    let lastOk = null
    for (const t of targets) {
      const doi = t.dispatch_order_item_id
      if (doi == null) continue
      const res = await setDispatchOrderItemMobileSlittingConfirmed(doi, { confirmed: true })
      if (res.success && res.data) {
        mergeSlittingPatchIntoOrder(res.data)
        lastOk = res
        const oid = order.value?.dispatch_order_id
        if (oid != null) removeLegacySlittingId(oid, doi)
      } else {
        ElMessage.error(res.message || '保存确认状态失败')
        return
      }
    }
    if (lastOk) {
      const n = targets.length
      ElMessage.success(
        n > 1 ? `已标记本组合并组 ${n} 条「分卷」完成（已保存到服务器）` : '已标记本条「分卷」完成（已保存到服务器）'
      )
      viewMode.value = 'toc'
      syncLineQuery()
    }
  } catch (e) {
    ElMessage.error(e.response?.data?.message || e.message || '保存确认状态失败')
  }
}

let touchStartX = 0
function onTouchStart(e) {
  touchStartX = e.changedTouches[0].screenX
}
async function onTouchEnd(e) {
  const x = e.changedTouches[0].screenX
  const dx = x - touchStartX
  if (Math.abs(dx) < 56) return
  if (dx > 0) await prevItem()
  else await nextItem()
}

async function prevItem() {
  if (currentIdx.value <= 0) return
  const ok = await ensureCurrentBranchSavedBeforeLeave()
  if (!ok) return
  currentIdx.value -= 1
  syncLineQuery()
}

async function nextItem() {
  if (currentIdx.value >= navItems.value.length - 1) return
  const ok = await ensureCurrentBranchSavedBeforeLeave()
  if (!ok) return
  currentIdx.value += 1
  syncLineQuery()
}

function resolveRollBatchId(row) {
  if (!row) return null
  const opts = assignedBatchOptions.value
  if (opts.length === 1) return opts[0].material_batch_id
  if (row.rollBatchId != null && row.rollBatchId !== '') {
    const n = Number(row.rollBatchId)
    return Number.isFinite(n) && n > 0 ? n : null
  }
  const usages = row.batchUsages || []
  const bids = []
  const seen = new Set()
  for (const u of usages) {
    const bid = u?.material_batch_id != null && u.material_batch_id !== '' ? Number(u.material_batch_id) : null
    if (!bid || seen.has(bid)) continue
    seen.add(bid)
    bids.push(bid)
  }
  return bids.length === 1 ? bids[0] : null
}

function applyFoldDefaultToRollBatch(row) {
  if (!row || !row.foldCompensation) return
  const rollId = resolveRollBatchId(row)
  if (rollId) row.foldCompensationBatchId = rollId
}

function onFoldCompensationChange(row, checked) {
  if (!row) return
  if (checked) applyFoldDefaultToRollBatch(row)
  else row.foldCompensationBatchId = null
}

function onRollBatchChanged(row) {
  if (!row) return
  // 勾选折补时，本卷料号变更则同步默认折补料号
  if (row.foldCompensation) applyFoldDefaultToRollBatch(row)
}

function emptyBranchRow() {
  const opts = assignedBatchOptions.value
  const onlyId = opts.length === 1 ? opts[0].material_batch_id : null
  return {
    branch_record_id: null,
    branch_pack_code: '',
    branch_label: '',
    quantityInput: '',
    branch_material_notes: '',
    foldCompensation: false,
    foldCompensationQtyInput: '',
    foldCompensationBatchId: null,
    rollBatchId: onlyId,
    batchUsages: onlyId
      ? [{ material_batch_id: onlyId, quantityInput: '' }]
      : [{ material_batch_id: null, quantityInput: '' }],
    /** 来自接口的实测（包装称重页写入），只读展示 */
    _pack_weight_kg: null,
    _pack_dims: ''
  }
}

function addRollUsageLine(row) {
  if (!row.batchUsages) row.batchUsages = []
  row.batchUsages.push({ material_batch_id: null, quantityInput: '' })
}

function onBranchQtyChanged(row) {
  if (!row || allowMultiBatchSameRoll.value) return
  // 非混卷：单料号用量跟随卷数量（保存时也会强制对齐）
}

function mapBranchFromApi(b) {
  const usages = Array.isArray(b.batch_usages) ? b.batch_usages : []
  const mappedUsages = usages.length
    ? usages.map((u) => ({
        material_batch_id: u.material_batch_id != null ? Number(u.material_batch_id) : null,
        quantityInput:
          u.quantity != null && u.quantity !== ''
            ? branchQtyInputFromOrderUnit(u.quantity)
            : ''
      }))
    : [{ material_batch_id: null, quantityInput: branchQtyInputFromOrderUnit(b.quantity) }]
  const rollBatchId =
    mappedUsages.length === 1 && mappedUsages[0].material_batch_id
      ? mappedUsages[0].material_batch_id
      : mappedUsages[0]?.material_batch_id || null
  const foldOn = !!b.is_fold_compensation
  let foldBatchId =
    b.fold_compensation_batch_id != null ? Number(b.fold_compensation_batch_id) : null
  // 已勾折补但未存料号：默认本卷
  if (foldOn && !foldBatchId && rollBatchId) foldBatchId = rollBatchId
  return {
    branch_record_id: b.branch_record_id,
    branch_pack_code: b.branch_pack_code || '',
    branch_label: b.branch_label || '',
    quantityInput: branchQtyInputFromOrderUnit(b.quantity),
    branch_material_notes: b.branch_material_notes || '',
    foldCompensation: foldOn,
    foldCompensationQtyInput:
      b.fold_compensation_qty != null && b.fold_compensation_qty !== ''
        ? branchQtyInputFromOrderUnit(b.fold_compensation_qty)
        : '',
    foldCompensationBatchId: foldBatchId,
    rollBatchId,
    batchUsages: mappedUsages,
    _pack_weight_kg: b.weight_kg != null && b.weight_kg !== '' ? Number(b.weight_kg) : null,
    _pack_dims: formatBranchPackDimsFromApi(b)
  }
}

function buildBatchUsagesPayload(row) {
  const qty = parseBranchQtyInOrderUnit(row.quantityInput)
  const opts = assignedBatchOptions.value
  if (opts.length === 1) {
    return qty > 0
      ? [{ material_batch_id: opts[0].material_batch_id, quantity: qty }]
      : []
  }
  if (!allowMultiBatchSameRoll.value) {
    const bid = row.rollBatchId != null && row.rollBatchId !== '' ? Number(row.rollBatchId) : null
    if (!bid || qty <= 0) return []
    return [{ material_batch_id: bid, quantity: qty }]
  }
  const out = []
  const seen = new Set()
  for (const u of row.batchUsages || []) {
    const bid = u.material_batch_id != null && u.material_batch_id !== '' ? Number(u.material_batch_id) : null
    if (!bid || seen.has(bid)) continue
    seen.add(bid)
    const q = parseBranchQtyInOrderUnit(u.quantityInput)
    if (q > 0) out.push({ material_batch_id: bid, quantity: q })
  }
  return out
}

/** 外尺寸展示 L×W×H（与包装称重页一致） */
function formatBranchPackDimsFromApi(b) {
  if (!b) return ''
  const L = b.length_cm
  const W = b.width_cm
  const H = b.height_cm
  const has = [L, W, H].some((x) => x != null && x !== '' && !Number.isNaN(Number(x)))
  if (!has) return ''
  const fmt = (x) => {
    if (x == null || x === '' || Number.isNaN(Number(x))) return '?'
    return String(Math.round(Number(x)))
  }
  return `${fmt(L)}×${fmt(W)}×${fmt(H)}`
}

function branchPackMeasuredLine(row) {
  if (!row) return ''
  const parts = []
  if (row._pack_weight_kg != null && !Number.isNaN(Number(row._pack_weight_kg))) {
    parts.push(`本支称重 ${fmtNumMaybe1(row._pack_weight_kg)} kg`)
  }
  if (row._pack_dims) parts.push(`外尺寸 ${row._pack_dims} cm`)
  return parts.join(' · ')
}

function syncWarehousePackedFromOrder(data) {
  warehousePackedForm.warehouse_packed_weight_str =
    data.warehouse_packed_weight_kg != null ? fmtNumMaybe1(data.warehouse_packed_weight_kg) : ''
  warehousePackedForm.warehouse_packed_length_str =
    data.warehouse_packed_length_cm != null ? String(data.warehouse_packed_length_cm) : ''
  warehousePackedForm.warehouse_packed_width_str =
    data.warehouse_packed_width_cm != null ? String(data.warehouse_packed_width_cm) : ''
  warehousePackedForm.warehouse_packed_height_str =
    data.warehouse_packed_height_cm != null ? String(data.warehouse_packed_height_cm) : ''
}

function warehousePackedPayloadOnly() {
  const parseFloatOrNull = (s) => {
    if (s == null || String(s).trim() === '') return null
    const n = parseFloat(String(s).replace(',', '.'))
    if (Number.isNaN(n)) return null
    return Math.round(n * 10) / 10
  }
  const parseIntOrNull = (s) => {
    if (s == null || String(s).trim() === '') return null
    const n = parseInt(String(s).trim(), 10)
    return Number.isNaN(n) ? null : n
  }
  const o = order.value
  if (!o) return {}
  const cur = current.value
  const reqW = !!(cur?.packaging_require_weigh ?? o.packaging_require_weigh)
  const reqV = !!(cur?.packaging_require_volume ?? o.packaging_require_volume)
  const out = {}
  if (reqW) {
    out.warehouse_packed_weight_kg = parseFloatOrNull(warehousePackedForm.warehouse_packed_weight_str)
  }
  if (reqV) {
    out.warehouse_packed_length_cm = parseIntOrNull(warehousePackedForm.warehouse_packed_length_str)
    out.warehouse_packed_width_cm = parseIntOrNull(warehousePackedForm.warehouse_packed_width_str)
    out.warehouse_packed_height_cm = parseIntOrNull(warehousePackedForm.warehouse_packed_height_str)
  }
  return out
}

/** 将派工明细 id 映射到合并后的导航下标（合并组内任一条 id 均指向同一 nav 行） */
function navIndexForDispatchOrderItemId(doi) {
  if (doi == null || !items.value.length) return -1
  const raw = items.value
  const ri = raw.findIndex((it) => Number(it.dispatch_order_item_id) === Number(doi))
  if (ri < 0) return -1
  const hit = raw[ri]
  const mk = hit.merge_group_key
  if (mk != null && String(mk).trim() !== '') {
    const key = String(mk).trim()
    return navItems.value.findIndex(
      (it) => it.merge_group_key != null && String(it.merge_group_key).trim() === key
    )
  }
  return navItems.value.findIndex((it) => Number(it.dispatch_order_item_id) === Number(doi))
}

async function loadOrder(preferDispatchOrderItemId = null) {
  if (!isDispatchDetailRoute()) return
  const id = Number(route.params.id)
  if (!Number.isFinite(id) || id <= 0) {
    return
  }
  loading.value = true
  try {
    const res = await getDispatchOrder(id, {
      params: { enforce_my_dispatch: 'true', require_audit_approved: 'true' }
    })
    if (res.success && res.data) {
      const keepDoi = preferDispatchOrderItemId ?? current.value?.dispatch_order_item_id
      order.value = res.data
      syncWarehousePackedFromOrder(res.data)
      if (keepDoi != null && items.value.length) {
        const nidx = navIndexForDispatchOrderItemId(keepDoi)
        if (nidx >= 0) currentIdx.value = nidx
      }
      if (navItems.value.length && currentIdx.value >= navItems.value.length) {
        currentIdx.value = 0
      }
      syncInvSelectionFromCurrent()
      await migrateLegacySlittingToServer()
    } else {
      ElMessage.error(res.message || '加载失败')
    }
  } catch (e) {
    const st = e.response?.status
    const msg = e.response?.data?.message || e.message || '加载失败'
    if (st === 403) {
      ElMessage.warning(msg)
      router.replace(mPath('dispatch'))
      return
    }
    ElMessage.error(msg)
  } finally {
    loading.value = false
  }
}

async function loadBranchesForCurrent() {
  const cur = current.value
  const doi = cur ? getBranchOwnerDispatchOrderItemId(cur) : null
  if (!doi) {
    branchRows.value = [emptyBranchRow()]
    syncInvSelectionFromCurrent()
    markBranchSnapshotSaved()
    return
  }
  try {
    const r = await getDispatchOrderItemBranchRecords(doi)
    const list = r.success && Array.isArray(r.data) ? r.data : []
    if (list.length) {
      branchRows.value = list.map((b) => mapBranchFromApi(b))
    } else {
      branchRows.value = [emptyBranchRow()]
    }
  } catch {
    branchRows.value = [emptyBranchRow()]
  }
  syncInvSelectionFromCurrent()
  markBranchSnapshotSaved()
}

watch(currentIdx, () => {
  loadBranchesForCurrent()
})

watch(selectedInvLocationId, () => {
  syncAdjustBatchForSelectedSlot()
})

watch(
  () => navItems.value.length,
  (n) => {
    if (n && currentIdx.value >= n) currentIdx.value = 0
  }
)

watch(
  () => route.params.id,
  async (newId) => {
    // keep-alive：非本页路由（如加工详情）也会改 params.id，须忽略以免误调派工 API
    if (!isDispatchDetailRoute()) return
    // keep-alive：离开详情回到 /m/dispatch 列表时 id 变为 undefined，不可再请求 /api/dispatch-orders/NaN
    if (newId == null || String(newId).trim() === '') return
    const nid = Number(newId)
    if (!Number.isFinite(nid) || nid <= 0) return
    currentIdx.value = 0
    await loadOrder()
    applyInitialLineFromRoute()
    await loadBranchesForCurrent()
  }
)

async function addRow() {
  branchRows.value.push(emptyBranchRow())
  await nextTick()
  addBranchFooterRef.value?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
}

function removeRow(ri) {
  branchRows.value.splice(ri, 1)
}

async function saveBranches(options = {}) {
  if (!canOperateDispatch.value) {
    ElMessage.warning('您没有保存分码权限，请联系管理员')
    return false
  }
  const { silent = false, toastOnSuccess = true } = options
  const cur = current.value
  const doi = cur ? getBranchOwnerDispatchOrderItemId(cur) : null
  if (!doi) return false
  savingBranches.value = true
  try {
    const payload = branchRows.value.map((r, i) => {
      let foldQty = null
      if (r.foldCompensation) {
        const raw = String(r.foldCompensationQtyInput ?? '').trim()
        if (raw !== '') {
          const n = parseBranchQtyInOrderUnit(r.foldCompensationQtyInput)
          foldQty = Number.isNaN(n) ? null : n
        }
      }
      let foldBatchId = null
      if (r.foldCompensation) {
        if (r.foldCompensationBatchId != null && r.foldCompensationBatchId !== '') {
          foldBatchId = Number(r.foldCompensationBatchId)
        } else {
          foldBatchId = resolveRollBatchId(r)
          if (foldBatchId) r.foldCompensationBatchId = foldBatchId
        }
      }
      const rollId = resolveRollBatchId(r)
      const row = {
        line_no: i + 1,
        quantity: parseBranchQtyInOrderUnit(r.quantityInput),
        branch_material_notes: r.branch_material_notes,
        is_fold_compensation: !!r.foldCompensation,
        fold_compensation_qty: foldQty,
        fold_compensation_batch_id: foldBatchId,
        roll_batch_id: rollId,
        batch_usages: buildBatchUsagesPayload(r)
      }
      if (r.branch_record_id) row.branch_record_id = r.branch_record_id
      return row
    })
    for (let i = 0; i < payload.length; i++) {
      const p = payload[i]
      if (p.is_fold_compensation) {
        if (!p.fold_compensation_batch_id) {
          if (!silent) ElMessage.warning(`第${i + 1}条请选择折补整扣料号（默认本卷）`)
          return false
        }
        if (!(p.fold_compensation_qty > 0)) {
          if (!silent) ElMessage.warning(`第${i + 1}条折补数量须大于 0`)
          return false
        }
      }
      if (p.quantity > 0 && (!p.batch_usages || !p.batch_usages.length)) {
        if (!silent) ElMessage.warning(`第${i + 1}条请选择本卷用料料号`)
        return false
      }
    }
    const res = await saveDispatchOrderItemBranchRecords(doi, {
      branches: payload,
      inventory_verified: !inventoryUnverified.value
    })
    if (res.success) {
      if (toastOnSuccess) ElMessage.success('分码已保存')
      await loadOrder(doi)
      await loadBranchesForCurrent()
      markBranchSnapshotSaved()
      return true
    }
    if (!silent) ElMessage.error(res.message || '保存失败')
    return false
  } catch (e) {
    if (!silent) ElMessage.error(e.response?.data?.message || e.message || '保存失败')
    return false
  } finally {
    savingBranches.value = false
  }
}

async function confirmPhysicalRunMatch() {
  if (!canAuditReview.value) {
    ElMessage.warning('您没有实跑核验权限，请联系管理员')
    return
  }
  const doi = current.value?.dispatch_order_item_id
  const slot = selectedInvSlot.value
  if (!doi || !slot || !selectedInvLocationId.value) {
    ElMessage.warning('请先选择库位')
    return
  }
  if (inventoryUnverified.value) return
  try {
    await ElMessageBox.confirm(
      '确认本库位现场实跑数量与上方「参考账面」一致？将在系统库存该料号/库位上标记「实跑核对」，并勾选本行已核验。',
      '确认实跑',
      { type: 'warning', confirmButtonText: '确认', cancelButtonText: '取消' }
    )
  } catch {
    return
  }
  confirmingPhysicalRun.value = true
  try {
    const payload = { storage_location_id: selectedInvLocationId.value }
    const batches = slot.batches || []
    if (isMultiBatchDispatch.value && batches.length > 1) {
      payload.confirm_all_batches_at_location = true
    } else {
      const row = batches[0] || slot
      const wh = (row.warehouse_manual_code || '').trim()
      if (wh && wh !== '未分配') payload.warehouse_manual_code = wh
      if (row.material_batch_id) payload.material_batch_id = row.material_batch_id
    }
    const res = await confirmDispatchItemPhysicalRun(doi, payload)
    if (res.success) {
      ElMessage.success(res.message || '已标记实跑核对')
      inventoryUnverified.value = false
      await loadOrder()
    } else {
      ElMessage.error(res.message || '操作失败')
    }
  } catch (e) {
    ElMessage.error(e.response?.data?.message || e.message || '操作失败')
  } finally {
    confirmingPhysicalRun.value = false
  }
}

async function saveWarehousePacked() {
  if (!canOperateDispatch.value) {
    ElMessage.warning('您没有保存包装实测权限，请联系管理员')
    return
  }
  const id = Number(route.params.id)
  savingPackaging.value = true
  try {
    const res = await saveDispatchWarehousePackaging(id, warehousePackedPayloadOnly())
    if (res.success) {
      ElMessage.success('实测数据已保存')
      if (res.data) {
        order.value = { ...order.value, ...res.data }
        syncWarehousePackedFromOrder(res.data)
      }
    } else {
      ElMessage.error(res.message || '保存失败')
    }
  } catch (e) {
    ElMessage.error(e.response?.data?.message || e.message || '保存失败')
  } finally {
    savingPackaging.value = false
  }
}

async function setStatus(st) {
  if (!canOperateDispatch.value) {
    ElMessage.warning('您没有更新派工状态权限，请联系管理员')
    return
  }
  // 仓库已提交完工后禁止改回进行中（与后端守卫一致；避免陈旧页面误点「开始作业」）
  if (warehouseCompletionSubmitted.value && ['draft', 'assigned', 'in_progress'].includes(st)) {
    ElMessage.warning('仓库已完工，不能改回未完工状态')
    return
  }
  try {
    const res = await updateDispatchOrderStatus(Number(route.params.id), { status: st })
    if (res.success) {
      ElMessage.success('状态已更新')
      await loadOrder()
    } else {
      ElMessage.error(res.message || '更新失败')
    }
  } catch (e) {
    ElMessage.error(e.response?.data?.message || e.message || '更新失败')
  }
}

async function submitWarehouseBizReview() {
  if (!canOperateDispatch.value) {
    ElMessage.warning('您没有完成工单权限，请联系管理员')
    return
  }
  if (!allSlittingLinesConfirmed.value) {
    ElMessage.warning('请先完成全部明细的「本分卷确认」')
    return
  }
  if (dispatchRemainingQty.value > BRANCH_QTY_MATCH_EPS) {
    ElMessage.warning(
      `全单剩余应打为 ${dispatchRemainingQtyDisplay.value}，须将分码合计补齐至订单数量后再提交业务审查`
    )
    return
  }
  try {
    await ElMessageBox.confirm(
      isProcessingOutboundDispatch.value
        ? '提交后将按分码数量扣减加工投入料库存并结案本派工单（无需打码业务核销）。'
        : '提交后业务员将进行核销：通过则结案；不通过将退回本单，您可修改后再次提交。提交后移动端将锁定编辑。',
      isProcessingOutboundDispatch.value ? '确认加工出库' : '确认提交业务审查',
      { type: 'warning', confirmButtonText: '确认提交', cancelButtonText: '取消' }
    )
  } catch {
    return
  }
  await completeJob(false)
}

async function completeJob(force, storageLocationId = null) {
  if (!canOperateDispatch.value) {
    ElMessage.warning('您没有完成工单权限，请联系管理员')
    return
  }
  if (dispatchRemainingQty.value > BRANCH_QTY_MATCH_EPS) {
    ElMessage.warning(
      `全单剩余应打为 ${dispatchRemainingQtyDisplay.value}，须补齐分码后再提交（不可强制绕过）`
    )
    return
  }
  const id = Number(route.params.id)
  completing.value = true
  try {
    const payload = {
      warehouse_completion_notes: completeNotes.value || undefined,
      force: !!force,
      ...warehousePackedPayloadOnly()
    }
    if (storageLocationId != null) {
      payload.storage_location_id = Number(storageLocationId)
    }
    const res = await warehouseCompleteDispatchOrder(id, payload)
    if (res.success) {
      ElMessage.success(
        isProcessingOutboundDispatch.value
          ? res.message || '已加工出库并结案'
          : '已提交业务员核销，移动端已锁定编辑'
      )
      await loadOrder()
      return
    }
    if (res.need_location_selection) {
      outboundLocPendingForce.value = !!force
      outboundLocPayload.value = res
      outboundLocDialogVisible.value = true
      return
    }
    ElMessage.error(res.message || '无法完成')
  } catch (e) {
    const data = e.response?.data
    if (data?.need_location_selection) {
      outboundLocPendingForce.value = !!force
      outboundLocPayload.value = data
      outboundLocDialogVisible.value = true
      return
    }
    if (data?.warnings?.length && e.response?.status === 400) {
      try {
        await ElMessageBox.confirm(
          `${data.warnings.join('\n')}\n\n是否仍要强制完成？`,
          '数量不一致',
          { type: 'warning', confirmButtonText: '强制完成', cancelButtonText: '取消' }
        )
        await completeJob(true)
      } catch {
        /* cancel */
      }
    } else {
      ElMessage.error(data?.message || e.message || '无法完成')
    }
  } finally {
    completing.value = false
  }
}

async function onOutboundLocationPicked(locationId) {
  await completeJob(outboundLocPendingForce.value, locationId)
}

async function submitPostCompletionAudit(action) {
  if (!canAuditReview.value) {
    ElMessage.warning('您没有派工单现场核销权限，请联系管理员')
    return
  }
  const id = order.value?.dispatch_order_id
  if (id == null) return
  if (action === 'approve') {
    const rem = Number(qtySummary.value?.dispatch_remaining_quantity ?? 0)
    if (rem > BRANCH_QTY_MATCH_EPS) {
      ElMessage.warning(`全单剩余应打为 ${fmtNumMaybe1(rem)}，不可核销通过，请退回仓库补齐分码`)
      return
    }
  }
  const tip =
    action === 'approve' ? '确认现场数据无误并核销通过？' : '确定退回修改？派工单将恢复为进行中。'
  try {
    await ElMessageBox.confirm(tip, action === 'approve' ? '核销通过' : '核销退回', {
      type: 'warning'
    })
  } catch {
    return
  }
  postCompletionLoading.value = true
  try {
    const res = await postCompletionAuditDispatchOrder(id, {
      action,
      audit_notes: postCompletionNotes.value || ''
    })
    if (res.success) {
      ElMessage.success(res.message || '已保存')
      postCompletionNotes.value = ''
      await loadOrder()
    } else {
      ElMessage.error(res.message || '操作失败')
    }
  } catch (e) {
    ElMessage.error(e.response?.data?.message || e.message || '操作失败')
  } finally {
    postCompletionLoading.value = false
  }
}

async function submitRevokePostCompletionAudit() {
  if (!canRevokeVerification.value) {
    ElMessage.warning('您没有「派工单-取消核销」权限，请联系管理员')
    return
  }
  const id = order.value?.dispatch_order_id
  if (id == null) return
  postCompletionRevokeLoading.value = true
  try {
    let reason = ''
    try {
      const { value } = await ElMessageBox.prompt(
        '取消后派工单将恢复为「进行中」，仓库可再次修改分码并提交完成。可选填原因（将写入备注）。',
        '取消核销',
        {
          confirmButtonText: '下一步',
          cancelButtonText: '关闭',
          inputType: 'textarea',
          inputPlaceholder: '原因（可选）'
        }
      )
      reason = String(value || '').trim()
    } catch (e) {
      if (e === 'cancel') return
      throw e
    }
    await ElMessageBox.confirm(
      `确定取消派工单「${order.value.dispatch_order_no}」的现场核销（已通过）吗？`,
      '确认取消核销',
      { type: 'warning', confirmButtonText: '确定', cancelButtonText: '取消' }
    )
    const res = await revokePostCompletionAuditDispatchOrder(id, { reason })
    if (res.success) {
      ElMessage.success(res.message || '已取消核销')
      await loadOrder()
    } else {
      ElMessage.error(res.message || '操作失败')
    }
  } catch (e) {
    if (e !== 'cancel') {
      ElMessage.error(e.response?.data?.message || e.message || '操作失败')
    }
  } finally {
    postCompletionRevokeLoading.value = false
  }
}

onMounted(async () => {
  userStore.initFromStorage()
  await loadMeta()
  await loadOrder()
  applyInitialLineFromRoute()
  await loadBranchesForCurrent()
})

onActivated(async () => {
  if (!isDispatchDetailRoute()) return
  const nid = Number(route.params.id)
  if (!Number.isFinite(nid) || nid <= 0) return
  await loadOrder()
  applyInitialLineFromRoute()
  await loadBranchesForCurrent()
})

/** 从「包装称重」子路由返回本页时重新拉分码（含称重/外尺寸） */
watch(
  () => route.path,
  async (newPath, oldPath) => {
    if (!isDispatchDetailRoute()) return
    if (!String(newPath).match(/^\/m\/dispatch\/\d+$/)) return
    if (!oldPath || !String(oldPath).includes('/packaging')) return
    await loadOrder()
    await loadBranchesForCurrent()
  }
)
</script>

<style scoped>
.m-page {
  max-width: 100%;
  width: 100%;
  margin: 0 auto;
  box-sizing: border-box;
  overflow-x: hidden;
  min-width: 0;
}

.m-back {
  margin-bottom: 8px;
}

.m-back--split {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
}

.m-section--toc {
  padding-bottom: 16px;
}

.m-section--biz-submit {
  border: 1px solid #bbf7d0;
  background: linear-gradient(180deg, #f0fdf4 0%, #fff 48%);
}

.m-section--post-audit {
  border: 1px solid #fde68a;
  background: linear-gradient(180deg, #fffbeb 0%, #fff 48%);
}

.m-post-audit-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.m-toc-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 6px;
}

.m-toc-head__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.m-toc-progress {
  font-size: 13px;
  color: #64748b;
}

.m-toc-progress strong {
  color: #059669;
  font-variant-numeric: tabular-nums;
}

.m-toc-hint {
  margin: 0 0 12px;
  font-size: 12px;
  line-height: 1.45;
}

.m-toc-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.m-toc-row {
  display: flex;
  align-items: stretch;
  gap: 10px;
  width: 100%;
  margin: 0;
  padding: 10px 12px;
  text-align: left;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #fff;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  transition: border-color 0.15s ease, background 0.15s ease;
}

.m-toc-row:active {
  background: #f8fafc;
}

.m-toc-row.is-current-line {
  border-color: #93c5fd;
  background: #eff6ff;
}

.m-toc-row__idx {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  margin-top: 2px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: #f1f5f9;
  font-size: 13px;
  font-weight: 700;
  color: #475569;
}

.m-toc-row__main {
  flex: 1;
  min-width: 0;
}

.m-toc-row__title {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
}

.m-toc-row__mat {
  margin-top: 4px;
  font-size: 12px;
  line-height: 1.4;
  word-break: break-word;
  color: #475569;
}

.m-toc-row__loc,
.m-toc-row__notes {
  margin-top: 4px;
  font-size: 12px;
  line-height: 1.45;
  word-break: break-word;
  color: #64748b;
}

.m-toc-row__code {
  word-break: break-all;
  overflow-wrap: anywhere;
  min-width: 0;
}

.m-toc-row__line {
  margin-top: 4px;
  font-size: 12px;
  line-height: 1.4;
  word-break: break-word;
}

.m-toc-row__chev {
  flex-shrink: 0;
  align-self: center;
  font-size: 18px;
  font-weight: 600;
  color: #94a3b8;
}

.m-toc-tick {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #ecfdf5;
  color: #059669;
  font-size: 13px;
  font-weight: 800;
  line-height: 1;
  flex-shrink: 0;
}

.m-toc-inline-nav {
  margin-bottom: 12px;
}

.m-toc-inline-nav--footer {
  margin-top: 4px;
  margin-bottom: 12px;
  padding-top: 8px;
  border-top: 1px dashed #e2e8f0;
}

.m-hero {
  background: #fff;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.m-hero__no {
  font-size: 18px;
  font-weight: 700;
}

.m-hero__sub {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 8px;
  font-size: 15px;
}

.m-hero__season {
  margin-top: 6px;
  font-size: 13px;
  line-height: 1.4;
}

.m-hero__due {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  font-size: 13px;
}

.m-hero__due-time {
  color: #334155;
  font-weight: 600;
}

.m-hero__due.m-due--critical .m-hero__due-time {
  color: #7f1d1d;
}

.m-hero__due.m-due--today .m-hero__due-time {
  color: #fb7185;
}

.m-hero__due.m-due--overdue .m-hero__due-time {
  color: #9a3412;
}

.m-hero__due.m-due--future .m-hero__due-time {
  color: #15803d;
}

.m-hero__due.m-due--completed-ok .m-hero__due-time {
  color: #166534;
}

.m-hero__sum {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #e2e8f0;
  font-size: 14px;
}

.m-hero__sum-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 6px;
}

.m-hero__sum-row .lbl {
  color: #64748b;
}

.m-hero__sum-row.strong {
  font-weight: 700;
  color: #0f172a;
}

.m-hero__done {
  margin: 8px 0 0;
  font-size: 12px;
  color: #64748b;
}

.m-hero__edit-tip {
  margin: 8px 0 0;
  font-size: 12px;
  color: #b45309;
}

.m-section {
  background: #fff;
  border-radius: 12px;
  padding: 12px;
  margin-bottom: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  max-width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
  min-width: 0;
}

.m-section--carousel {
  padding: 18px 16px 20px;
  min-height: 120px;
}

.m-section--tip {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
}

.m-section--kickback .m-kickback-pending-meta {
  margin: 8px 0 0;
}

.m-section--kickback .m-kickback-reason-preview {
  margin: 8px 0 0;
  font-size: 13px;
  line-height: 1.45;
  word-break: break-word;
}

.m-tip-icon {
  font-size: 18px;
  line-height: 1.2;
}

.m-tip-text {
  margin: 0;
  font-size: 13px;
  color: #1e3a5f;
  line-height: 1.5;
}

.m-section-head-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 8px;
  flex-wrap: wrap;
}

.m-h3--row {
  margin: 0;
  flex: 1;
  min-width: 0;
}

.m-h3 {
  margin: 0 0 10px;
  font-size: 15px;
  font-weight: 600;
}

.m-step-nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.m-step-nav--large {
  margin-top: 4px;
}

.m-step-btn {
  min-width: 56px !important;
  height: 56px !important;
  font-size: 28px !important;
  font-weight: 700;
}

.m-step-nav__center {
  flex: 1;
  text-align: center;
  min-width: 0;
}

.m-step-nav__txt-wrap {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  flex-wrap: wrap;
}

.m-step-nav__txt {
  font-size: 17px;
  font-weight: 700;
}

.m-toc-kickback {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
  color: #b45309;
  background: #fffbeb;
  border: 1px solid #fcd34d;
  border-radius: 4px;
}

.m-step-nav__hint {
  font-size: 12px;
  color: #94a3b8;
  margin-top: 4px;
}

.m-kv {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 14px;
  margin-bottom: 8px;
  max-width: 100%;
  min-width: 0;
}

.m-kv .k {
  color: #64748b;
  flex-shrink: 0;
  max-width: 38%;
}

.m-kv .v {
  text-align: right;
  min-width: 0;
  flex: 1;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.m-kv .v.strong {
  font-weight: 700;
  color: #0f172a;
}

.m-kv--block {
  flex-direction: column;
  align-items: stretch;
}

.m-kv--block .v {
  text-align: left;
  margin-top: 4px;
  white-space: pre-wrap;
  word-break: break-word;
}

.m-order-line-notes .m-note-block {
  margin: 0 0 6px;
  font-size: 13px;
  line-height: 1.45;
  white-space: pre-wrap;
  word-break: break-word;
}

.m-order-line-notes .m-note-block:last-child {
  margin-bottom: 0;
}

.m-note-tag {
  display: inline-block;
  margin-right: 6px;
  padding: 0 6px;
  border-radius: 4px;
  background: #f1f5f9;
  color: #475569;
  font-size: 11px;
  font-weight: 600;
  vertical-align: middle;
}

.m-toc-row__ord-notes {
  font-size: 11px;
  line-height: 1.35;
  margin-top: 2px;
}

.m-batch-lines {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.m-batch-line {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 4px;
  font-size: 13px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f1f5f9;
}

.m-batch-line:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.m-batch-line__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
}

.m-batch-line__input {
  display: flex;
  align-items: center;
  gap: 8px;
}

.m-batch-line__readonly {
  margin-top: 2px;
}

.m-batch-actual-input {
  flex: 1;
  min-width: 0;
}

.m-batch-actual-input :deep(.el-input__wrapper) {
  border-radius: 10px;
}

.m-batch-actions {
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
}

.m-batch-tip {
  margin-bottom: 6px;
}

.m-locs {
  margin-top: 8px;
  font-size: 13px;
  color: #334155;
}

.m-loc-line {
  padding: 8px 0;
  border-bottom: 1px solid #f1f5f9;
}

.m-split-h {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
}

.m-hint {
  font-size: 12px;
  color: #64748b;
  margin: 0 0 10px;
}

.m-inline-link {
  color: var(--el-color-primary);
  font-weight: 600;
}

.m-pack-row {
  margin-bottom: 12px;
}

.m-pack-radios {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.m-pack-switches {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 12px;
}

.m-pack-measures {
  background: #f8fafc;
  border-radius: 10px;
  padding: 12px;
  margin-bottom: 12px;
}

.m-pack-measures__title {
  margin: 0 0 10px;
  font-size: 13px;
  font-weight: 600;
  color: #334155;
}

.m-pack-dims {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 8px;
}

.m-pack-field {
  margin-bottom: 8px;
}

.m-branch-pack-measured {
  margin-top: 6px;
  line-height: 1.4;
}

.m-branch-card {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 10px;
  margin-bottom: 10px;
  max-width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
  min-width: 0;
}

.m-branch-card__head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  font-weight: 600;
}

.m-branch-card__actions {
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
}

.m-branch-pack-code {
  font-family: ui-monospace, monospace;
  font-size: 12px;
  color: #0f172a;
  margin: 0 0 8px;
  word-break: break-all;
  line-height: 1.4;
}

.m-branch-label-code {
  font-family: ui-monospace, monospace;
  font-size: 13px;
  font-weight: 700;
  color: #1d4ed8;
  margin: 0 0 8px;
  word-break: break-all;
  line-height: 1.4;
}

.m-branch-pack-pending {
  margin: 0 0 8px;
}

.m-add-branch {
  margin: 6px 0 14px;
}

.m-mb-sm {
  margin-bottom: 8px;
}

.m-mt {
  margin-top: 10px;
}

.m-run-tag {
  font-size: 13px;
  margin: 0 0 8px;
}

.m-mb {
  margin-bottom: 8px;
}

.m-lbl {
  display: block;
  font-size: 12px;
  color: #64748b;
  margin-bottom: 4px;
}

.m-fw {
  width: 100%;
  max-width: 100%;
  min-width: 0;
}

/* iOS：字号 ≥16px 避免聚焦时整页放大，键盘相对更可控 */
.m-fw :deep(.el-input__inner),
.m-roll-usage-line__qty :deep(.el-input__inner),
.m-roll-usage-line__batch :deep(.el-select__selected-item),
.m-roll-usage-line__batch :deep(.el-select__placeholder),
.m-roll-usage-line__batch :deep(.el-select__input) {
  font-size: 16px !important;
}

.m-recon {
  background: #f0fdf4;
  border-radius: 8px;
  padding: 10px 12px;
  margin: 12px 0;
  font-size: 14px;
}

.m-recon.warn {
  background: #fffbeb;
}

.m-recon__row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;
}

.m-recon__row.strong {
  font-weight: 700;
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px dashed #cbd5e1;
}

.m-recon .num {
  font-variant-numeric: tabular-nums;
}

.m-warn-text {
  margin: 8px 0 0;
  font-size: 12px;
  color: #b45309;
}

.m-qty-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.m-qty-cur {
  margin: 0 0 8px;
  font-size: 14px;
  color: #475569;
}

.m-qty-tip {
  margin: 0 0 12px;
  font-size: 12px;
  color: #94a3b8;
  line-height: 1.5;
}

.m-qty-form :deep(.el-form-item) {
  margin-bottom: 14px;
}

.m-qty-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.m-qty-plain {
  flex: 1;
  min-width: 0;
}

.m-qty-plain :deep(.el-input__wrapper) {
  border-radius: 10px;
}

.m-qty-plain :deep(.el-input__inner) {
  font-size: 16px;
  font-weight: 600;
  text-align: left;
}

.m-qty-step {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex-shrink: 0;
}

.m-qty-step .el-button {
  margin: 0;
  min-width: 52px;
  padding: 6px 8px;
}

.m-fold-row {
  margin-top: 8px;
}

.m-mix-roll-alert {
  margin-bottom: 8px;
}

.m-roll-batch__single {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
}

.m-roll-usage-line {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
  max-width: 100%;
  min-width: 0;
}

.m-roll-usage-line__batch {
  flex: 1 1 100%;
  min-width: 0;
  max-width: 100%;
}

.m-roll-usage-line__qty {
  flex: 1 1 calc(100% - 52px);
  min-width: 0;
  max-width: 100%;
}

@media (min-width: 420px) {
  .m-roll-usage-line__batch {
    flex: 1.4 1 0;
  }
  .m-roll-usage-line__qty {
    flex: 1 1 0;
  }
}

.m-muted-tip {
  margin: 8px 0 0;
  font-size: 12px;
  color: #64748b;
  line-height: 1.45;
}

.m-inv-unverified {
  display: block;
  margin: 12px 0 8px;
}

.m-mt {
  margin-top: 10px;
}

.muted {
  color: #94a3b8;
  font-size: 13px;
}
</style>
